/**
 * HUD layer — the two-tier console (Sprint 10): a passive READOUT BAR over a
 * COMMAND DOCK of three framed zones (BUILD / WAVE / ABILITIES), a contextual
 * tower-selection panel, transient notices and the win / lose / pause overlays.
 * Styled as a neon system-diagnostics console: monospace readouts, glowing
 * meters, crisp end-of-run panels.
 *
 * One `hudLayout()` function is the single source of truth for every dock
 * rectangle — `drawHud` and `Game`'s pointer hit-tests consume it identically,
 * so the drawn controls and their hit-boxes can never drift. The HUD only
 * reads state.
 */
import {
  CONFIG,
  type AbilityId,
  type Difficulty,
  type SpawnType,
  type TowerType,
  type WaveDef,
} from '../config';
import { clamp, roundRect, withAlpha } from '../engine/util';
import { LOCALES, LOCALE_META, cycleLocale, getLocale, t } from '../i18n';
import type { Tower } from '../game/entities';
import {
  isDifficultyUnlocked,
  isModeUnlocked,
  isTowerUnlocked,
  type Profile,
} from '../game/profile';
import { ENDLESS_MODIFIERS } from '../game/endless';
import type { AbilityRuntime, GameMode, GameState } from '../game/state';
import { genWave } from '../game/systems';
import { getPuzzleLevel } from '../game/puzzles';

/** Identifiers for the clickable COMMAND DOCK buttons. */
export type HudButtonId =
  | 'tower-pulse'
  | 'tower-arc'
  | 'tower-cryo'
  | 'tower-pylon'
  | 'tower-mortar'
  | 'tower-sniper'
  | 'tower-wall'
  | 'start'
  | 'speed'
  | 'mute';

/** A plain clickable rectangle. */
interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Playfield origin (top-left), below the HUD strip — for tower-anchored UI. */
const OY = CONFIG.hudHeight;
/** Tile size, in pixels. */
const TS = CONFIG.grid.tileSize;

/** The six core tower types in hotkey order 1·2·3·4·5·6. */
const TOWER_ORDER: readonly TowerType[] = ['pulse', 'arc', 'pylon', 'cryo', 'mortar', 'sniper'];

/**
 * The BUILD-zone chip order for a given run mode. Maze Mode appends the Wall
 * (hotkey 7) as a seventh chip; the fixed-path modes show only the six core
 * placeables, since the Wall has no route to shape there (§16.9).
 */
function chipOrder(mode: GameMode): readonly TowerType[] {
  return mode === 'maze' ? [...TOWER_ORDER, 'wall'] : TOWER_ORDER;
}

/** Tower type a tower button maps to (or null for a run-control button). */
export function towerOfButton(id: HudButtonId): TowerType | null {
  if (id === 'tower-pulse') return 'pulse';
  if (id === 'tower-arc') return 'arc';
  if (id === 'tower-cryo') return 'cryo';
  if (id === 'tower-pylon') return 'pylon';
  if (id === 'tower-mortar') return 'mortar';
  if (id === 'tower-sniper') return 'sniper';
  if (id === 'tower-wall') return 'wall';
  return null;
}

// --- command-dock layout — the single source of truth -----------------------

/** Height of the tier-1 READOUT BAR (passive telemetry). */
const READOUT_H = 40;
/** Top y of the three COMMAND DOCK zone panels. */
const ZONE_Y = 44;
/** Height of a COMMAND DOCK zone panel. */
const ZONE_H = 70;

/** One tower chip in the BUILD zone. */
interface TowerChipRect {
  id: HudButtonId;
  type: TowerType;
  rect: Rect;
  /** True when this tower is still unlock-gated for the profile (§19.2). */
  locked: boolean;
}

/** The Overload dial's clickable sub-rects (build phase, ABILITIES zone). */
interface OverloadDialRects {
  minus: Rect;
  plus: Rect;
  /** One rect per Overload tier; the array index is the tier it selects. */
  segments: Rect[];
}

/**
 * Canvas-space geometry of the whole COMMAND DOCK. Computed once per call and
 * consumed by both the renderer and every pointer hit-test (the pattern the
 * old `overloadPanelLayout` used, extended to the entire HUD). The Overload
 * dial and the ability slots share the ABILITIES zone — both sets of rects are
 * always returned; the draw / hit-test pick by phase.
 */
interface HudLayout {
  buildZone: Rect;
  waveZone: Rect;
  abilitiesZone: Rect;
  /** The six BUILD-zone tower chips, in hotkey order. */
  towerChips: TowerChipRect[];
  /** The WAVE zone's primary action button (start / pause / restart). */
  start: Rect;
  /** The WAVE zone's speed toggle. */
  speed: Rect;
  /** The WAVE zone's mute toggle. */
  mute: Rect;
  /** The build-phase Overload dial controls (ABILITIES zone). */
  overload: OverloadDialRects;
  /** The wave-phase ability slots (ABILITIES zone). */
  abilitySlots: Rect[];
}

/**
 * Canvas-space layout of the COMMAND DOCK — see {@link HudLayout}. The BUILD
 * zone holds six tower chips, or seven in Maze Mode (the extra Wall chip,
 * §16.9), so the chip width is mode-dependent; everything else is fixed.
 *
 * When a `profile` is supplied each tower chip is flagged `locked` if the
 * tower is still unlock-gated for that profile (§19.2); pointer hit-tests pass
 * no profile (they only need geometry) so their chips report `locked: false`.
 */
export function hudLayout(mode: GameMode = 'campaign', profile?: Profile): HudLayout {
  const buildZone: Rect = { x: 6, y: ZONE_Y, w: 384, h: ZONE_H };
  const waveZone: Rect = { x: 396, y: ZONE_Y, w: 160, h: ZONE_H };
  const abilitiesZone: Rect = { x: 562, y: ZONE_Y, w: 232, h: ZONE_H };

  // BUILD — six tower chips (seven in Maze Mode), taller than the old 30px row
  // (Fitts, §1.4). The Maze chip set is slightly narrower so the extra Wall
  // chip fits the same zone; the fixed-path layout is unchanged.
  const chips = chipOrder(mode);
  const chipGap = 4;
  const chipW = chips.length > 6 ? 49 : 58;
  const chipX0 = buildZone.x + 8;
  const towerChips: TowerChipRect[] = chips.map((type, i) => ({
    id: `tower-${type}` as HudButtonId,
    type,
    rect: { x: chipX0 + i * (chipW + chipGap), y: 62, w: chipW, h: 46 },
    locked: profile !== undefined && !isTowerUnlocked(profile, type),
  }));

  // WAVE — the largest dock target (Start), an incoming-wave preview line,
  // then two small secondary toggles along the foot of the zone.
  const start: Rect = { x: waveZone.x + 5, y: 58, w: 150, h: 30 };
  const speed: Rect = { x: waveZone.x + 5, y: 100, w: 50, h: 12 };
  const mute: Rect = { x: waveZone.x + 59, y: 100, w: 96, h: 12 };

  // ABILITIES — the Overload dial (build) and ability slots (wave) share the
  // zone; they are time-exclusive, so both rect sets are computed up front.
  const dialY = 58;
  const dialH = 18;
  const stepW = 18;
  const minus: Rect = { x: abilitiesZone.x + 10, y: dialY, w: stepW, h: dialH };
  const plus: Rect = {
    x: abilitiesZone.x + abilitiesZone.w - 10 - stepW,
    y: dialY,
    w: stepW,
    h: dialH,
  };
  const segCount = CONFIG.overloadTiers.length;
  const segGap = 3;
  const segX0 = minus.x + stepW + 4;
  const segSpan = plus.x - 4 - segX0;
  const segW = (segSpan - segGap * (segCount - 1)) / segCount;
  const segments: Rect[] = [];
  for (let i = 0; i < segCount; i += 1) {
    segments.push({ x: segX0 + i * (segW + segGap), y: dialY, w: segW, h: dialH });
  }

  const slotCount = 3;
  const slotGap = 7;
  const slotW = (abilitiesZone.w - 20 - slotGap * (slotCount - 1)) / slotCount;
  const abilitySlots: Rect[] = [];
  for (let i = 0; i < slotCount; i += 1) {
    abilitySlots.push({
      x: abilitiesZone.x + 10 + i * (slotW + slotGap),
      y: 58,
      w: slotW,
      h: 50,
    });
  }

  return {
    buildZone,
    waveZone,
    abilitiesZone,
    towerChips,
    start,
    speed,
    mute,
    overload: { minus, plus, segments },
    abilitySlots,
  };
}

/** True when (x, y) lies inside the rect `r`. */
function inRect(r: Rect, x: number, y: number): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

/** Which COMMAND DOCK button (tower chip / run control) sits under (x, y). */
export function hudButtonAt(x: number, y: number, mode: GameMode = 'campaign'): HudButtonId | null {
  const layout = hudLayout(mode);
  for (const chip of layout.towerChips) {
    if (inRect(chip.rect, x, y)) return chip.id;
  }
  if (inRect(layout.start, x, y)) return 'start';
  if (inRect(layout.speed, x, y)) return 'speed';
  if (inRect(layout.mute, x, y)) return 'mute';
  return null;
}

/** What an Overload-dial click targets — a `−` / `+` button or a tier. */
export type OverloadHit = { kind: 'minus' } | { kind: 'plus' } | { kind: 'tier'; tier: number };

/**
 * Hit-test a click against the build-phase Overload dial in the ABILITIES
 * zone. Returns the targeted control, or null when the click misses or the
 * dial is not on screen (it shows only in the build phase from wave 1 on).
 */
export function overloadPanelAt(x: number, y: number, state: GameState): OverloadHit | null {
  if (state.phase !== 'build' || state.wave < 1) return null;
  const dial = hudLayout().overload;
  if (inRect(dial.minus, x, y)) return { kind: 'minus' };
  if (inRect(dial.plus, x, y)) return { kind: 'plus' };
  for (let i = 0; i < dial.segments.length; i += 1) {
    if (inRect(dial.segments[i]!, x, y)) return { kind: 'tier', tier: i };
  }
  return null;
}

/**
 * Hit-test a click against the wave-phase ability slots in the ABILITIES
 * zone. Returns the ability id, or null when the click misses or no wave is
 * running (the slots show only in the wave phase).
 */
export function abilitySlotAt(x: number, y: number, state: GameState): AbilityId | null {
  if (state.phase !== 'wave') return null;
  const slots = hudLayout().abilitySlots;
  for (let i = 0; i < state.abilities.length; i += 1) {
    const slot = slots[i];
    if (slot && inRect(slot, x, y)) return state.abilities[i]!.id;
  }
  return null;
}

/**
 * Short, button-width tower names — pulled live from the i18n catalogue so
 * a locale switch updates the chips. The config labels are too long for the
 * chip and are NEVER read here (config is the canonical English brand name).
 */
function towerShort(type: TowerType): string {
  return t(`towers.${type}.short`);
}

const C = CONFIG.colors;
const FONT_DISPLAY = '"Chakra Petch", "Rajdhani", system-ui, sans-serif';
const FONT_MONO = '"Share Tech Mono", ui-monospace, "DejaVu Sans Mono", monospace';

/** Overload tier 0→4 escalation ramp (mirrors render.ts) — calm cyan to red. */
const OVERLOAD_RAMP: readonly string[] = [
  '#2de2e6',
  '#ffd166',
  '#ff8c42',
  '#ff5630',
  '#ff4d6d',
];

// --- title selector + win-continue geometry ----------------------------------

/**
 * Title-screen Campaign / Endless / Maze chips. Docked over the upper circuit
 * trace so a click here is hit-tested as a mode pick before any tile routing,
 * never confused with placing a tower during the wave-0 build phase. The y
 * values track the playfield origin (`OY`), which the taller Sprint-10 HUD
 * strip shifted down +36.
 */
const MODE_CAMPAIGN_RECT: Rect = { x: 158, y: 202, w: 92, h: 30 };
const MODE_ENDLESS_RECT: Rect = { x: 256, y: 202, w: 92, h: 30 };
const MODE_MAZE_RECT: Rect = { x: 354, y: 202, w: 92, h: 30 };
/**
 * Sprint 57 (puzzle-enable-s57) — the fourth RUN MODE chip, PUZZLE. Mirrors
 * the 98-px stride of the three existing chips (Campaign / Endless / Maze),
 * landing exactly on the Sprint-53 puzzle-chip hover rect so the hover hint
 * and the visible chip share the same canvas-space footprint. The selector
 * module's outer panel widens to enclose it; see `drawTitleBanner`.
 */
const MODE_PUZZLE_RECT: Rect = { x: 452, y: 202, w: 92, h: 30 };

/**
 * Sprint 70 (mode-clarity-impl-s70) — a violet accent for the Puzzle chip,
 * per `docs/MODE_CLARITY_DESIGN.md` §F1. The other three modes reuse
 * existing palette tokens (`C.accent` / `C.warning` / `C.good`); Puzzle is
 * the one mode whose colour didn't already exist in `CONFIG.colors`, so
 * it lives as a local constant here rather than widening the palette
 * (config.ts is off-limits for this sprint per the brief).
 */
const MODE_PUZZLE_ACCENT = '#b66dff';

/**
 * Sprint 70 — per-mode accent palette for the RUN MODE chip row. Drives
 * `SelectorChip.accent` so the four mode chips read as four distinct
 * affordances at a glance (see `docs/MODE_CLARITY_DESIGN.md` §F1):
 *   - Campaign keeps cyan (the brand / active-glow colour),
 *   - Endless picks up amber for the "escalating" read,
 *   - Maze takes green for the "you-shape-the-board" mode,
 *   - Puzzle takes a new violet that signals "different game entirely".
 */
function modeAccent(mode: GameMode): string {
  if (mode === 'endless') return C.warning;
  if (mode === 'maze') return C.good;
  if (mode === 'puzzle') return MODE_PUZZLE_ACCENT;
  return C.accent;
}

/**
 * Title-screen Easy / Normal / Hard chips, docked over the lower circuit trace
 * (path row 9) — likewise clear of every buildable tile.
 */
const DIFF_EASY_RECT: Rect = { x: 368, y: 482, w: 72, h: 30 };
const DIFF_NORMAL_RECT: Rect = { x: 446, y: 482, w: 72, h: 30 };
const DIFF_HARD_RECT: Rect = { x: 524, y: 482, w: 72, h: 30 };

/** Win-screen "Continue → Endless" button — centred on the win overlay panel. */
const WIN_CONTINUE_RECT: Rect = { x: 280, y: 356, w: 240, h: 32 };

/**
 * A one-line summary of each run mode for the title-screen descriptor —
 * resolved via the i18n catalogue (`modeBlurb.<id>`).
 */
function modeBlurb(mode: GameMode): string {
  return t(`modeBlurb.${mode}`);
}

/** Flux cost of a locked run mode / difficulty, for the title-screen lock badge. */
function unlockCost(predicate: (id: string) => boolean): number {
  return CONFIG.unlocks.find((n) => predicate(n.id))?.cost ?? 0;
}

/**
 * Flux cost to unlock weapon tower `type` from the meta-progression tree
 * (§19.2), or 0 when the tower is a free starter never in the tree. Drives the
 * BUILD-zone locked-chip badge and the onboarding tooltip's unlock guidance.
 */
export function towerUnlockCost(type: TowerType): number {
  const node = CONFIG.unlocks.find(
    (n) => n.grants.kind === 'tower' && n.grants.tower === type,
  );
  return node?.cost ?? 0;
}

/**
 * Flux cost to unlock active ability `id` from the meta-progression tree
 * (§19.2), or 0 when the ability is a free starter never in the tree
 * (Overcharge). Mirrors {@link towerUnlockCost}; drives the onboarding
 * ability tooltip's unlock-path block.
 */
export function abilityUnlockCost(id: AbilityId): number {
  const node = CONFIG.unlocks.find(
    (n) => n.grants.kind === 'ability' && n.grants.ability === id,
  );
  return node?.cost ?? 0;
}

/** The circular `?` Help button, top-right of the READOUT BAR. */
const HELP_BUTTON = { cx: 780, cy: 21, r: 12 };

/**
 * Sprint 73 (tutorial-impl-s73) — title-screen Start Tutorial button rect.
 * Per `docs/TUTORIAL_DESIGN.md` §2 the affordance lives on the title-screen,
 * prominent, "near Profile / Mode picker". The Sprint 60 design doc proposed
 * `{ x: 252, y: 296, w: 296, h: 38 }` — but that slot is now claimed by the
 * Sprint 70 mode-descriptor pill (sits at y 250 to ~y 290). We anchor BELOW
 * the DIFFICULTY module (which ends at y 458 + 64 = 522) instead, so we
 * never collide with the chip row Sprint 70 / 72 just shipped, while still
 * landing on the title screen as the centred primary call to action.
 */
const TUTORIAL_BUTTON_RECT: Rect = { x: 252, y: 530, w: 296, h: 40 };

/** Hit-test the title-screen Start Tutorial button; gated on `isTitleScreen`. */
export function tutorialButtonAt(x: number, y: number, state: GameState): boolean {
  if (!isTitleScreen(state)) return false;
  // Sprint 76 (modal-occlusion-fix-s76) — when a tower is armed for placement,
  // the title-screen chips fade and become click-pass-through so the player
  // can place a tower on the playfield row beneath them. See
  // `titleSelectorsDimmed()` for the corresponding visual fade in drawHud.
  if (titleSelectorsDimmed(state)) return false;
  return inRect(TUTORIAL_BUTTON_RECT, x, y);
}

/** True while the wave-0 title screen (with the mode / difficulty toggle) shows. */
function isTitleScreen(state: GameState): boolean {
  return state.phase === 'build' && state.wave === 0;
}

/**
 * Sprint 76 (modal-occlusion-fix-s76) — true when the title-screen selectors
 * (RUN MODE chips, DIFFICULTY chips, Start-Tutorial button, mode-info hover
 * card, locale chip, map chip) should fade out and stop intercepting clicks
 * so the player can place a tower on the playfield row beneath them.
 *
 * Background: the selector chips paint OVER the playfield grid (mode row at
 * y≈202 sits on top of tile rows 2–3; difficulty row at y≈482 sits on rows
 * 9–10; Start-Tutorial at y≈530 sits on rows 10–11). With the original
 * always-modal routing in {@link tutorialButtonAt} / {@link modeToggleAt} /
 * {@link difficultyToggleAt}, a click that visually targeted a tile under one
 * of those rects was eaten by the selector instead of placing the armed
 * tower — the player perceived the selectors as "overlapping" / blocking
 * gameplay even before wave 1.
 *
 * The fix: while a tower is armed for placement (`state.selectedTowerType !==
 * null`), the selectors render at ~30 % opacity AND fall through to the
 * playfield. The player's intent (place a tower) wins; the selectors are
 * still visible so the player can confirm what's underneath but no longer
 * eat clicks. Releasing the armed tower (Escape, place success, switch mode)
 * restores the chips' full state.
 */
export function titleSelectorsDimmed(state: GameState): boolean {
  return isTitleScreen(state) && state.selectedTowerType !== null;
}

/**
 * Title-screen locale switcher chip — a tiny LANG chip docked on the upper-
 * left of the title playfield, well clear of the mode chips and the Profile
 * launcher. Visible only on the wave-0 title screen.
 *
 * Sprint 58 (i18n-10langs-s58) — the chip was originally a two-segment
 * `EN | CS` toggle (Sprint 56b), but with 12 shipped locales a side-by-side
 * row no longer fits. The chip is now a **click-to-cycle** affordance: it
 * shows the current locale's short code (`EN`, `CS`, `DE`, …, `KO`) plus a
 * small `↻` cue. Each click advances to the next locale in `LOCALES` and
 * wraps at the end. Chip dimensions are unchanged.
 *
 * Geometry is canvas-space; click hit-testing reads {@link localeChipAt}.
 */
const LOCALE_CHIP_RECT: Rect = { x: 18, y: 204, w: 90, h: 26 };

/** Hit-test the title-screen locale chip; returns `true` when (x, y) hits it. */
export function localeChipAt(x: number, y: number, state: GameState): boolean {
  if (!isTitleScreen(state)) return false;
  // Sprint 76 — click-pass-through while a tower is armed for placement.
  if (titleSelectorsDimmed(state)) return false;
  return inRect(LOCALE_CHIP_RECT, x, y);
}

/** The run mode whose title-screen chip is under (x, y), or null. */
export function modeToggleAt(x: number, y: number, state: GameState): GameMode | null {
  if (!isTitleScreen(state)) return null;
  // Sprint 76 — click-pass-through while a tower is armed for placement so
  // the click can hit the playfield tile underneath rather than re-arming a
  // mode that's already selected (see {@link titleSelectorsDimmed}).
  if (titleSelectorsDimmed(state)) return null;
  if (inRect(MODE_CAMPAIGN_RECT, x, y)) return 'campaign';
  if (inRect(MODE_ENDLESS_RECT, x, y)) return 'endless';
  if (inRect(MODE_MAZE_RECT, x, y)) return 'maze';
  if (inRect(MODE_PUZZLE_RECT, x, y)) return 'puzzle';
  return null;
}

/** The difficulty whose title-screen chip is under (x, y), or null. */
export function difficultyToggleAt(x: number, y: number, state: GameState): Difficulty | null {
  if (!isTitleScreen(state)) return null;
  // Sprint 76 — click-pass-through while a tower is armed for placement.
  if (titleSelectorsDimmed(state)) return null;
  if (inRect(DIFF_EASY_RECT, x, y)) return 'easy';
  if (inRect(DIFF_NORMAL_RECT, x, y)) return 'normal';
  if (inRect(DIFF_HARD_RECT, x, y)) return 'hard';
  return null;
}

/** True when (x, y) hits the win screen's Continue → Endless button. */
export function winContinueAt(x: number, y: number, state: GameState): boolean {
  return state.phase === 'won' && inRect(WIN_CONTINUE_RECT, x, y);
}

/** True when (x, y) hits the `?` Help button. */
export function helpButtonAt(x: number, y: number): boolean {
  const dx = x - HELP_BUTTON.cx;
  const dy = y - HELP_BUTTON.cy;
  return dx * dx + dy * dy <= HELP_BUTTON.r * HELP_BUTTON.r;
}

/**
 * Sprint 54 (cheat-profile-s54) — draw the two unobtrusive test-mode pips in
 * the READOUT BAR strip, just left of the `?` Help button:
 *   - `🔓 CHEAT` when `cheatActive` (the `?cheat=unlock-all` URL param or a
 *     runtime `cheatUnlockAll()` call has been applied to this profile);
 *   - `👤 <name>` when `profileName` is a non-default named profile from
 *     `?profile=<name>`.
 *
 * Both pips are tiny, drawn as outlined chips at the right end of the
 * READOUT BAR (y=0–40). They never overlap the COMMAND DOCK below, never
 * intercept clicks (no hit-test), and never appear in the default-profile-
 * non-cheat path so a normal player session is bit-for-bit Sprint-53.
 */
export function drawCheatProfilePips(
  ctx: CanvasRenderingContext2D,
  cheatActive: boolean,
  profileName: string | undefined,
): void {
  // Right-aligned, starting just left of the `?` Help button (cx=780, r=12),
  // leaving an 8-px gutter. Each pip is drawn rightmost-first.
  let rightEdge = HELP_BUTTON.cx - HELP_BUTTON.r - 8;
  const y = 8;
  const h = 22;
  if (cheatActive) {
    rightEdge -= drawTinyPip(ctx, rightEdge, y, h, t('hud.cheatPip'), C.warning) + 6;
  }
  if (profileName) {
    drawTinyPip(ctx, rightEdge, y, h, t('hud.profilePip', { name: profileName }), C.accent);
  }
}

/**
 * Render one tiny pip (label inside a thin outlined rounded rect) right-
 * anchored at `rightEdge`. Returns the pip's width so the caller can stack
 * additional pips leftward. Pure paint — no interaction.
 */
function drawTinyPip(
  ctx: CanvasRenderingContext2D,
  rightEdge: number,
  y: number,
  h: number,
  text: string,
  color: string,
): number {
  ctx.save();
  ctx.font = `11px ${FONT_MONO}`;
  const textW = Math.ceil(ctx.measureText(text).width);
  const w = textW + 14;
  const x = rightEdge - w;
  ctx.fillStyle = 'rgba(10, 18, 36, 0.78)';
  roundRect(ctx, x, y, w, h, 5);
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = withAlpha(color, 0.75);
  ctx.shadowBlur = 4;
  ctx.shadowColor = color;
  roundRect(ctx, x + 0.5, y + 0.5, w - 1, h - 1, 5);
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x + w / 2, y + h / 2 + 1);
  ctx.restore();
  return w;
}

/**
 * Draw the whole HUD: the two-tier console (READOUT BAR + COMMAND DOCK), the
 * contextual selection panel, transient notices and end-of-run overlays.
 *
 * Sprint 70 (mode-clarity-impl-s70) — accepts an optional `pointer` so the
 * title-screen RUN MODE chips can paint a hover info card under the chip
 * the cursor is over. Omitted at call sites that don't have pointer state
 * (e.g. headless tests) — the hover card is opt-in.
 */
export function drawHud(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  profile: Profile,
  pointer?: { x: number; y: number },
): void {
  const w = CONFIG.canvas.width;
  const time = performance.now() / 1000;

  // --- strip background -----------------------------------------------------
  const g = ctx.createLinearGradient(0, 0, 0, CONFIG.hudHeight);
  g.addColorStop(0, '#0d1426');
  g.addColorStop(1, '#080d1a');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, CONFIG.hudHeight);

  // Tier 1 — the READOUT BAR reads as a slightly lighter inset band, formally
  // separated from the interactive COMMAND DOCK below it (§4.1).
  const rg = ctx.createLinearGradient(0, 0, 0, READOUT_H);
  rg.addColorStop(0, '#121d36');
  rg.addColorStop(1, '#0c1426');
  ctx.fillStyle = rg;
  ctx.fillRect(0, 0, w, READOUT_H);

  // Neon divider between HUD and playfield.
  ctx.save();
  ctx.strokeStyle = withAlpha(C.accent, 0.85);
  ctx.lineWidth = 2;
  ctx.shadowBlur = 10;
  ctx.shadowColor = C.accent;
  ctx.beginPath();
  ctx.moveTo(0, CONFIG.hudHeight - 1);
  ctx.lineTo(w, CONFIG.hudHeight - 1);
  ctx.stroke();
  ctx.restore();
  // Thin top accent.
  ctx.fillStyle = withAlpha(C.accent, 0.25);
  ctx.fillRect(0, 0, w, 1);

  // Neon hairline along the READOUT BAR's bottom edge — the tier 1 | 2 split.
  ctx.save();
  ctx.strokeStyle = withAlpha(C.accent, 0.7);
  ctx.lineWidth = 2;
  ctx.shadowBlur = 8;
  ctx.shadowColor = C.accent;
  ctx.beginPath();
  ctx.moveTo(0, READOUT_H);
  ctx.lineTo(w, READOUT_H);
  ctx.stroke();
  ctx.restore();

  drawStats(ctx, state);
  drawPowerBar(ctx, state, time);
  drawHelpButton(ctx, time);
  drawCommandDock(ctx, state, profile, time);
  drawSelectionPanel(ctx, state);
  drawTitleBanner(ctx, state, profile, time);
  // Sprint 70 — the mode-pick hover info card. Painted after the title
  // banner so it sits on top of the chip row + descriptor pill, and only
  // resolves on the wave-0 title screen (`modeToggleAt` is itself gated
  // by `isTitleScreen`). When the cursor isn't over any mode chip, this
  // is a no-op.
  //
  // Sprint 76 (modal-occlusion-fix-s76) — also suppressed while a tower is
  // armed for placement, so the hover card never grows over the playfield
  // tiles the player is about to click. `modeToggleAt` already returns null
  // in that state via {@link titleSelectorsDimmed}, so this branch is dead
  // code in the armed path; the explicit gate documents the contract.
  if (pointer && !titleSelectorsDimmed(state)) {
    const hoveredMode = modeToggleAt(pointer.x, pointer.y, state);
    if (hoveredMode) drawModeInfoCard(ctx, hoveredMode);
  }
  drawNotice(ctx, state);
  drawOverlay(ctx, state, profile, time);
}

/** The circular `?` Help button in the HUD's top-right corner. */
function drawHelpButton(ctx: CanvasRenderingContext2D, time: number): void {
  const { cx, cy, r } = HELP_BUTTON;
  const pulse = 0.5 + 0.5 * Math.sin(time * 2.2);
  ctx.save();
  ctx.fillStyle = '#101a30';
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = withAlpha(C.accent, 0.6 + 0.3 * pulse);
  ctx.lineWidth = 1.4;
  ctx.shadowBlur = 6 + 4 * pulse;
  ctx.shadowColor = C.accent;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.fillStyle = C.accent;
  ctx.font = `700 14px ${FONT_DISPLAY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('?', cx, cy + 1);
  ctx.restore();
}

// --- title selector (wave-0 mode + difficulty) -------------------------------

/** One selectable chip in a title-screen module. */
interface SelectorChip {
  rect: Rect;
  label: string;
  sub: string;
  active: boolean;
  /** Per-chip neon accent. */
  accent: string;
  /** True when this option is still unlock-gated (§19.2) — shows a lock badge. */
  locked: boolean;
  /**
   * Sprint 70 (mode-clarity-impl-s70) — optional run-mode tag that asks the
   * chip to render the mode's identifying glyph in the top-right corner.
   * Set for the four mode chips, omitted for the difficulty chips (whose
   * three colours already differentiate them). See {@link drawModeGlyph}.
   */
  glyph?: GameMode;
}

/**
 * The wave-0 title selector: two neon control modules docked on the circuit
 * traces — a Campaign / Endless run-mode toggle on the upper trace and an
 * Easy / Normal / Hard difficulty toggle on the lower trace. Both sit over
 * path tiles, so they never steal a click from a buildable tile.
 */
function drawTitleBanner(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  profile: Profile,
  time: number,
): void {
  if (!isTitleScreen(state)) return;

  // Sprint 76 (modal-occlusion-fix-s76) — when a tower is armed for placement
  // the chips fade to ~30% opacity AND stop intercepting clicks (see
  // {@link titleSelectorsDimmed}). This way the chips don't visually block
  // the playfield row beneath them and the player's placement click lands on
  // the tile, not on a re-arm of the already-selected mode.
  const dimmed = titleSelectorsDimmed(state);
  ctx.save();
  if (dimmed) ctx.globalAlpha = 0.3;

  const endlessCost = unlockCost((id) => id === 'mode.endless');
  const mazeCost = unlockCost((id) => id === 'mode.maze');
  const hardCost = unlockCost((id) => id === 'difficulty.hard');

  // Sprint 57 (puzzle-enable-s57) — the RUN MODE module grows from a 3-chip
  // module (w: 304) to a 4-chip module (w: 402) to enclose the new PUZZLE
  // chip in the natural 4th column. The chip rects themselves keep the same
  // 98-px stride that the prior three chips established, so Campaign /
  // Endless / Maze hit-boxes are byte-identical with Sprint-56.
  drawSelectorModule(
    ctx,
    { x: 150, y: 178, w: 402, h: 64 },
    t('hud.runMode'),
    [
      {
        rect: MODE_CAMPAIGN_RECT,
        label: t('modes.campaign'),
        sub: t('modeSub.campaign'),
        active: state.mode === 'campaign',
        accent: modeAccent('campaign'),
        locked: false,
        glyph: 'campaign',
      },
      {
        rect: MODE_ENDLESS_RECT,
        label: t('modes.endless'),
        sub: t('modeSub.endless', { cost: endlessCost }),
        active: state.mode === 'endless',
        accent: modeAccent('endless'),
        locked: !isModeUnlocked(profile, 'endless'),
        glyph: 'endless',
      },
      {
        rect: MODE_MAZE_RECT,
        label: t('modes.maze'),
        sub: t('modeSub.maze', { cost: mazeCost }),
        active: state.mode === 'maze',
        accent: modeAccent('maze'),
        locked: !isModeUnlocked(profile, 'maze'),
        glyph: 'maze',
      },
      // Sprint 57 — Puzzle is unlock-free (Game.ts:playerSetMode exempts
      // 'puzzle' from the unlock gate) so `locked: false` always.
      {
        rect: MODE_PUZZLE_RECT,
        label: t('modes.puzzle'),
        sub: t('modeSub.puzzle'),
        active: state.mode === 'puzzle',
        accent: modeAccent('puzzle'),
        locked: false,
        glyph: 'puzzle',
      },
    ],
    time,
  );

  drawSelectorModule(
    ctx,
    { x: 352, y: 458, w: 260, h: 64 },
    t('hud.difficulty'),
    [
      {
        rect: DIFF_EASY_RECT,
        label: t('difficulty.easy'),
        sub: t('difficultySub.easy'),
        active: state.difficulty === 'easy',
        accent: C.good,
        locked: false,
      },
      {
        rect: DIFF_NORMAL_RECT,
        label: t('difficulty.normal'),
        sub: t('difficultySub.normal'),
        active: state.difficulty === 'normal',
        accent: C.accent,
        locked: false,
      },
      {
        rect: DIFF_HARD_RECT,
        label: t('difficulty.hard'),
        sub: t('difficultySub.hard', { cost: hardCost }),
        active: state.difficulty === 'hard',
        accent: C.bad,
        locked: !isDifficultyUnlocked(profile, 'hard'),
      },
    ],
    time,
  );

  drawModeDescriptor(ctx, state);
  drawLocaleChip(ctx, time);
  // Sprint 73 (tutorial-impl-s73) — the title-screen Start Tutorial button.
  // Reads the profile's `seenFullTutorial` flag for the label / pulse state
  // (first-time vs. resting "Replay tour"). Click handling routes through
  // {@link tutorialButtonAt} in `Game.ts`.
  drawTutorialButton(ctx, profile, time);
  // Sprint 76 — close the alpha wrap opened above so the dim fade only
  // affects the title-banner selectors, not any later HUD layers.
  ctx.restore();
}

/**
 * Sprint 73 (tutorial-impl-s73) — paint the title-screen Start Tutorial
 * button. Anchored at {@link TUTORIAL_BUTTON_RECT}; visible only on the
 * wave-0 title screen (`drawTitleBanner` gates this on `isTitleScreen`
 * upstream — same gate that hides the chip rows behind a running wave).
 *
 * The button has two resting states driven off `profile.seenFullTutorial`:
 *
 *   - **First-time** (`false`) — the bright "START TUTORIAL" label, a
 *     pulsing accent border, and the sub-line "First time? Take the tour".
 *     This is the player's primary call-to-action.
 *   - **Resting / seen** (`true`) — the calmer "REPLAY TOUR" label and a
 *     non-pulsing border; sub-line "Refresh the basics". Mirrors the
 *     Help-panel "Replay intro" pattern in `onboarding.ts`.
 *
 * Every visible string flows through the i18n catalogue under
 * `tutorial.button.{start,startSub,replay,replaySub}`. Paint-only — hit
 * testing lives in `tutorialButtonAt`.
 */
function drawTutorialButton(
  ctx: CanvasRenderingContext2D,
  profile: Profile,
  time: number,
): void {
  const r = TUTORIAL_BUTTON_RECT;
  const seen = profile.seenFullTutorial;
  const label = seen ? t('tutorial.button.replay') : t('tutorial.button.start');
  const sub = seen ? t('tutorial.button.replaySub') : t('tutorial.button.startSub');
  // Pulse only in the first-time state — the resting "Replay tour"
  // affordance should not compete with active gameplay affordances.
  const pulse = seen ? 0.7 : 0.6 + 0.4 * Math.sin(time * 3);
  const glow = seen ? 4 : 14 * pulse;
  const borderAlpha = seen ? 0.55 : 0.75 + 0.25 * pulse;
  const fillAlpha = seen ? 0.12 : 0.22;

  ctx.save();
  // Backing pill — slightly darker than the surrounding chip backings so
  // the bright accent border carries the focus on a first-time profile.
  const g = ctx.createLinearGradient(0, r.y, 0, r.y + r.h);
  g.addColorStop(0, 'rgba(13, 22, 42, 0.95)');
  g.addColorStop(1, 'rgba(8, 12, 26, 0.95)');
  ctx.fillStyle = g;
  roundRect(ctx, r.x, r.y, r.w, r.h, 9);
  ctx.fill();
  // Accent wash over the fill — picks up the pulse on the first-time path.
  ctx.fillStyle = withAlpha(C.accent, fillAlpha);
  roundRect(ctx, r.x, r.y, r.w, r.h, 9);
  ctx.fill();
  // Border + glow.
  ctx.strokeStyle = withAlpha(C.accent, borderAlpha);
  ctx.lineWidth = seen ? 1.3 : 1.7;
  ctx.shadowBlur = glow;
  ctx.shadowColor = C.accent;
  roundRect(ctx, r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1, 9);
  ctx.stroke();
  ctx.shadowBlur = 0;

  // Label + sub-line.
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `700 14px ${FONT_DISPLAY}`;
  ctx.fillStyle = C.accent;
  ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2 - 5);
  ctx.font = `9px ${FONT_MONO}`;
  ctx.fillStyle = withAlpha(C.accent, 0.78);
  ctx.fillText(sub, r.x + r.w / 2, r.y + r.h / 2 + 10);
  ctx.restore();
}

/**
 * Draw the title-screen locale chip — a click-to-cycle pill showing the
 * active locale's short code (`EN`, `CS`, `DE`, …, `KO`) plus a small `↻`
 * cue hinting that a click advances to the next locale. The chip is the
 * same overall size as the Sprint-56b two-segment toggle.
 *
 * Click handling lives in `Game.ts` via {@link localeChipAt} + {@link
 * applyLocaleChipClick}; this function is paint-only.
 */
function drawLocaleChip(ctx: CanvasRenderingContext2D, time: number): void {
  const r = LOCALE_CHIP_RECT;
  const active = getLocale();
  const idx = LOCALES.indexOf(active);
  const pulse = 0.45 + 0.25 * Math.sin(time * 2.2);

  ctx.save();
  // Backing pill — lit version with the active locale always shown highlit.
  ctx.fillStyle = 'rgba(8, 14, 28, 0.88)';
  roundRect(ctx, r.x, r.y, r.w, r.h, r.h / 2);
  ctx.fill();
  // The active segment fill — the whole pill highlights, since there's only
  // one "cell" with the cycle UX.
  ctx.fillStyle = withAlpha(C.accent, 0.18 + 0.06 * pulse);
  roundRect(ctx, r.x + 2, r.y + 2, r.w - 4, r.h - 4, (r.h - 4) / 2);
  ctx.fill();
  ctx.strokeStyle = withAlpha(C.accent, 0.55 + 0.25 * pulse);
  ctx.lineWidth = 1;
  roundRect(ctx, r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1, r.h / 2);
  ctx.stroke();

  // Tiny eyebrow caption + locale counter, anchored above the pill.
  // The counter ("3/12") doubles as a discoverability cue that the chip
  // cycles through more than the two locales the Sprint-56b version showed.
  ctx.font = `8px ${FONT_MONO}`;
  ctx.fillStyle = withAlpha(C.accent, 0.7);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(t('localeChip.label'), r.x + 4, r.y - 4);
  ctx.textAlign = 'right';
  ctx.fillText(`${idx + 1}/${LOCALES.length}`, r.x + r.w - 2, r.y - 4);

  // The active code, centred. Bold and cyan so it reads as a chip-style
  // monospaced "EN", "ZH", "中" alike (CJK languages send a 2-char code).
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `700 13px ${FONT_DISPLAY}`;
  ctx.fillStyle = withAlpha(C.accent, 0.95);
  ctx.fillText(LOCALE_META[active].code, r.x + r.w / 2 - 6, r.y + r.h / 2 + 1);

  // Cycle cue — small `↻` glyph on the right side of the pill.
  ctx.font = `11px ${FONT_MONO}`;
  ctx.fillStyle = withAlpha(C.accent, 0.55 + 0.25 * pulse);
  ctx.fillText('↻', r.x + r.w - 12, r.y + r.h / 2 + 1);
  ctx.restore();
}

/**
 * Public re-entry point — advance to the next shipped locale in `LOCALES`,
 * wrapping at the end. Used by `Game.ts`'s click handler when a click hits
 * the title-screen LANG chip.
 */
export function applyLocaleChipClick(): void {
  cycleLocale();
}

/**
 * A one-line summary of the selected run mode, in a soft pill below the RUN
 * MODE module — so the three-way toggle explains itself at a glance, Maze
 * especially (it leans on this rather than the campaign tutorial, §12).
 */
function drawModeDescriptor(ctx: CanvasRenderingContext2D, state: GameState): void {
  // Sprint 57 — RUN MODE module widened from w 304 → 402 (4 chips); the
  // centre moves from 302 → 351 so the descriptor pill stays under the
  // module centre even with the new PUZZLE chip in the 4th column.
  const cx = 351; // RUN MODE module centre (x 150 + w 402 / 2).
  ctx.save();
  ctx.font = `10px ${FONT_MONO}`;
  const lines = wrapLabel(ctx, modeBlurb(state.mode), 384);
  let textW = 0;
  for (const l of lines) textW = Math.max(textW, ctx.measureText(l).width);
  const lineH = 15;
  const w = textW + 32;
  const h = lines.length * lineH + 12;
  const x = cx - w / 2;
  const y = 250;
  // Pill.
  ctx.fillStyle = 'rgba(8, 12, 24, 0.82)';
  roundRect(ctx, x, y, w, h, 8);
  ctx.fill();
  ctx.strokeStyle = withAlpha(C.accent, 0.3);
  ctx.lineWidth = 1;
  roundRect(ctx, x, y, w, h, 8);
  ctx.stroke();
  // Text.
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = withAlpha(C.accent, 0.85);
  let ly = y + 6 + lineH / 2;
  for (const l of lines) {
    ctx.fillText(l, cx, ly);
    ly += lineH;
  }
  ctx.restore();
}

/**
 * Sprint 70 (mode-clarity-impl-s70) — the hover info card painted under the
 * RUN MODE chip the cursor is over. Compares all four modes pre-click by
 * surfacing the tagline / three "what's different" bullets / session length
 * / skill exercised, per `docs/MODE_CLARITY_DESIGN.md` §F3.
 *
 * Layered on top of {@link drawModeDescriptor}'s pill so that hover-state
 * dominates the static descriptor — the player gets one focused panel at a
 * time. Card is paint-only; the hover state is derived per frame from the
 * pointer + {@link modeToggleAt} so there is no extra state to maintain.
 *
 * Position: anchored under the hovered chip, horizontally clamped to the
 * canvas so the card never escapes. The card carries the mode's accent so
 * it inherits its identity (the same accent used in `drawSelectorChip`'s
 * per-mode tint, §F1).
 */
function drawModeInfoCard(ctx: CanvasRenderingContext2D, mode: GameMode): void {
  const rect =
    mode === 'campaign'
      ? MODE_CAMPAIGN_RECT
      : mode === 'endless'
        ? MODE_ENDLESS_RECT
        : mode === 'maze'
          ? MODE_MAZE_RECT
          : MODE_PUZZLE_RECT;
  const accent = modeAccent(mode);
  const tagline = t(`modeCard.${mode}.tagline`);
  const bullets = [
    t(`modeCard.${mode}.bullet1`),
    t(`modeCard.${mode}.bullet2`),
    t(`modeCard.${mode}.bullet3`),
  ];
  const lengthHint = t(`modeCard.${mode}.length`);
  const skillHint = t(`modeCard.${mode}.skill`);
  const label = t(`modes.${mode}`);

  const cardW = 340;
  // Header (~26) + tagline (~16) + divider (~10) + 3 bullets (~16 each) + skill (~18) + padding.
  const cardH = 26 + 16 + 10 + 16 * 3 + 18 + 14;
  // Anchor under the chip, with a 6-px gap; clamp horizontally so the card
  // never leaves the canvas bounds.
  let x = rect.x + rect.w / 2 - cardW / 2;
  x = clamp(x, 8, CONFIG.canvas.width - cardW - 8);
  const y = rect.y + rect.h + 8;

  ctx.save();
  // Backing pill — mirrors the descriptor-pill chrome (rgba 8/12/24 fill +
  // accent hairline) so the card reads as the HUD-language family member it
  // is, not a foreign tooltip surface.
  ctx.fillStyle = 'rgba(8, 12, 24, 0.94)';
  roundRect(ctx, x, y, cardW, cardH, 10);
  ctx.fill();
  ctx.strokeStyle = withAlpha(accent, 0.65);
  ctx.lineWidth = 1.2;
  ctx.shadowBlur = 10;
  ctx.shadowColor = withAlpha(accent, 0.55);
  roundRect(ctx, x + 0.5, y + 0.5, cardW - 1, cardH - 1, 10);
  ctx.stroke();
  ctx.shadowBlur = 0;

  // Header row: bold mode label (left) + session length hint (right).
  const headerY = y + 16;
  ctx.textBaseline = 'alphabetic';
  ctx.font = `700 13px ${FONT_DISPLAY}`;
  ctx.textAlign = 'left';
  ctx.fillStyle = accent;
  ctx.fillText(label, x + 14, headerY);
  ctx.font = `9px ${FONT_MONO}`;
  ctx.textAlign = 'right';
  ctx.fillStyle = withAlpha(accent, 0.85);
  ctx.fillText(lengthHint, x + cardW - 14, headerY);

  // Tagline — italic-feeling secondary line.
  const taglineY = headerY + 18;
  ctx.font = `10px ${FONT_MONO}`;
  ctx.textAlign = 'left';
  ctx.fillStyle = C.hudText;
  ctx.fillText(tagline, x + 14, taglineY);

  // Divider hairline.
  const dividerY = taglineY + 8;
  ctx.strokeStyle = withAlpha(accent, 0.3);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x + 14, dividerY);
  ctx.lineTo(x + cardW - 14, dividerY);
  ctx.stroke();

  // Three bullets — the "what's different here" block.
  ctx.font = `10px ${FONT_MONO}`;
  ctx.fillStyle = C.hudMuted;
  let bulletY = dividerY + 14;
  for (const bullet of bullets) {
    ctx.fillStyle = withAlpha(accent, 0.8);
    ctx.fillText('•', x + 16, bulletY);
    ctx.fillStyle = C.hudText;
    ctx.fillText(bullet, x + 26, bulletY);
    bulletY += 16;
  }

  // "Exercises X" skill line — small, muted, sits at the card's base.
  ctx.font = `9px ${FONT_MONO}`;
  ctx.fillStyle = withAlpha(accent, 0.7);
  ctx.fillText(skillHint, x + 14, bulletY + 4);
  ctx.restore();
}

/** Greedily wrap `text` to lines no wider than `maxWidth` (ctx font preset). */
function wrapLabel(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/** A framed title-screen control module: a header plus a row of chips. */
function drawSelectorModule(
  ctx: CanvasRenderingContext2D,
  panel: Rect,
  heading: string,
  chips: SelectorChip[],
  time: number,
): void {
  ctx.save();
  // Backing panel.
  const g = ctx.createLinearGradient(0, panel.y, 0, panel.y + panel.h);
  g.addColorStop(0, 'rgba(13, 20, 38, 0.95)');
  g.addColorStop(1, 'rgba(8, 12, 24, 0.95)');
  ctx.fillStyle = g;
  roundRect(ctx, panel.x, panel.y, panel.w, panel.h, 9);
  ctx.fill();
  ctx.strokeStyle = withAlpha(C.accent, 0.4 + 0.18 * Math.sin(time * 2.5));
  ctx.lineWidth = 1.3;
  ctx.shadowBlur = 12;
  ctx.shadowColor = withAlpha(C.accent, 0.5);
  roundRect(ctx, panel.x, panel.y, panel.w, panel.h, 9);
  ctx.stroke();
  ctx.shadowBlur = 0;

  // Header.
  const cx = panel.x + panel.w / 2;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.font = `9px ${FONT_MONO}`;
  ctx.fillStyle = C.hudMuted;
  ctx.fillText(spaced(heading), cx, panel.y + 16);

  for (const chip of chips) drawSelectorChip(ctx, chip, time);
  ctx.restore();
}

/** One title-screen selector chip (Campaign / Endless / Easy / Normal / Hard). */
function drawSelectorChip(
  ctx: CanvasRenderingContext2D,
  chip: SelectorChip,
  time: number,
): void {
  const r = chip.rect;
  ctx.save();
  // A still-locked option (§19.2) reads dim with a Flux-cost sub-line.
  if (chip.locked) ctx.globalAlpha = 0.5;
  if (chip.active) {
    const g = ctx.createLinearGradient(0, r.y, 0, r.y + r.h);
    g.addColorStop(0, withAlpha(chip.accent, 0.95));
    g.addColorStop(1, withAlpha(chip.accent, 0.6));
    ctx.fillStyle = g;
  } else {
    ctx.fillStyle = '#101a30';
  }
  roundRect(ctx, r.x, r.y, r.w, r.h, 6);
  ctx.fill();
  ctx.strokeStyle = chip.active ? chip.accent : withAlpha(chip.accent, 0.42);
  ctx.lineWidth = chip.active ? 1.8 : 1.1;
  if (chip.active) {
    ctx.shadowBlur = 12;
    ctx.shadowColor = chip.accent;
  }
  roundRect(ctx, r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1, 6);
  ctx.stroke();
  ctx.shadowBlur = 0;

  const cx = r.x + r.w / 2;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.font = `700 12px ${FONT_DISPLAY}`;
  ctx.fillStyle = chip.active ? '#06101f' : C.hudText;
  ctx.fillText(chip.label, cx, r.y + 14);
  ctx.font = `8px ${FONT_MONO}`;
  ctx.fillStyle = chip.locked
    ? C.warning
    : chip.active
      ? withAlpha('#06101f', 0.78)
      : C.hudMuted;
  ctx.fillText(chip.locked ? `${t('hud.locked')} ${chip.sub}` : chip.sub, cx, r.y + 24);
  // A soft "selected" pulse marker.
  if (chip.active && !chip.locked) {
    ctx.fillStyle = withAlpha('#06101f', 0.45 + 0.45 * Math.sin(time * 4));
    ctx.beginPath();
    ctx.arc(r.x + 8, r.y + r.h / 2, 2.3, 0, Math.PI * 2);
    ctx.fill();
  }
  // Sprint 70 — per-mode identifying glyph, drawn top-right of the chip
  // body. Inactive chips draw the glyph in the chip's accent colour so
  // each chip telegraphs its mode at a glance (per §F1 of MODE_CLARITY).
  // Active chips render the glyph in the dark on-active text colour so it
  // stays legible against the bright accent fill.
  if (chip.glyph) {
    drawModeGlyph(
      ctx,
      r.x + r.w - 11,
      r.y + 9,
      chip.glyph,
      chip.active ? '#06101f' : chip.accent,
    );
  }
  ctx.restore();
}

/**
 * Sprint 70 — render the per-mode glyph that identifies a RUN MODE chip.
 * Drawn ~10 px square centred at (`cx`, `cy`). Each shape names the mode
 * by verb (per `docs/MODE_CLARITY_DESIGN.md` §F1):
 *   - Campaign — three-segment S-path arrow (the fixed wave route),
 *   - Endless  — infinity loop (the unbounded run),
 *   - Maze     — chunky brick (you place walls to shape the path),
 *   - Puzzle   — 3×3 grid of dots (one solution among many cells).
 * Pure paint — no hit-test, no state read beyond the mode tag.
 */
function drawModeGlyph(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  mode: GameMode,
  color: string,
): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 1.2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (mode === 'campaign') {
    // Three short connected segments that read as a winding S.
    ctx.beginPath();
    ctx.moveTo(cx - 4.5, cy - 3);
    ctx.lineTo(cx - 1, cy - 3);
    ctx.lineTo(cx - 1, cy);
    ctx.lineTo(cx + 3, cy);
    ctx.lineTo(cx + 3, cy + 3);
    ctx.lineTo(cx + 5, cy + 3);
    ctx.stroke();
    // Tiny arrowhead at the end so the path reads as a route, not a stair.
    ctx.beginPath();
    ctx.moveTo(cx + 5, cy + 3);
    ctx.lineTo(cx + 3.5, cy + 1.6);
    ctx.moveTo(cx + 5, cy + 3);
    ctx.lineTo(cx + 3.5, cy + 4.4);
    ctx.stroke();
  } else if (mode === 'endless') {
    // Infinity glyph — two small joined ellipses.
    ctx.beginPath();
    ctx.ellipse(cx - 2.5, cy, 2.6, 2.2, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(cx + 2.5, cy, 2.6, 2.2, 0, 0, Math.PI * 2);
    ctx.stroke();
  } else if (mode === 'maze') {
    // A wall brick with a seam — pairs visually with the BUILD-zone Wall
    // tower glyph so the player wires "Maze = walls" subconsciously.
    roundRect(ctx, cx - 4.5, cy - 3.5, 9, 7, 1.2);
    ctx.fill();
    ctx.strokeStyle = withAlpha('#06101f', 0.5);
    ctx.lineWidth = 0.9;
    ctx.beginPath();
    ctx.moveTo(cx - 4.5, cy);
    ctx.lineTo(cx + 4.5, cy);
    ctx.stroke();
  } else if (mode === 'puzzle') {
    // 3×3 dot grid — reads as a logic-layout grid, picking up the
    // line-of-sight-wall + fixed-budget framing.
    for (let i = -1; i <= 1; i += 1) {
      for (let j = -1; j <= 1; j += 1) {
        ctx.beginPath();
        ctx.arc(cx + i * 3.2, cy + j * 3.2, 0.95, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
  ctx.restore();
}

// --- stat readouts -----------------------------------------------------------

/** Row 1, left: gold / lives / wave / score readout chips. */
function drawStats(ctx: CanvasRenderingContext2D, state: GameState): void {
  // Sprint 63 (puzzle-ux-briefing-s63) — puzzle-mode stat row swaps the
  // gold counter for an "X / Y" budget pip (live remaining / total budget)
  // and the wave counter for the puzzle's single-wave indicator. The
  // score chip is hidden under puzzle mode (the par row in the level
  // select is the player's real progression signal — Sprint 30 / 40).
  const puzzle = state.mode === 'puzzle';
  if (puzzle && state.puzzleLevelId) {
    const level = getPuzzleLevel(state.puzzleLevelId);
    const budget = level?.goldBudget ?? state.gold;
    statChip(
      ctx,
      14,
      drawCoinIcon,
      t('hud.credits'),
      `${state.gold} / ${budget}`,
      '#f7d51d',
    );
  } else {
    statChip(ctx, 14, drawCoinIcon, t('hud.credits'), String(state.gold), '#f7d51d');
  }
  statChip(ctx, 146, drawCoreIcon, t('hud.core'), String(state.lives), C.core);
  // Campaign counts toward 10; Endless just shows the depth, flagged ENDLESS.
  // Puzzle always shows 1 of 1 (puzzle levels are single-wave by spec).
  const endless = state.mode === 'endless';
  if (puzzle) {
    // Wave 0 (build phase): show "1 / 1" — the puzzle's single authored
    // wave is what the player is about to start. Wave 1 during the run is
    // the same authored wave; show identically.
    statChip(
      ctx,
      262,
      drawWaveIcon,
      t('hud.wave'),
      '1 / 1',
      C.hudText,
    );
  } else {
    statChip(
      ctx,
      262,
      drawWaveIcon,
      endless ? t('hud.endless') : t('hud.wave'),
      endless ? String(state.wave) : `${state.wave} / ${CONFIG.waves.length}`,
      endless ? C.accent : C.hudText,
    );
  }
  if (!puzzle) {
    statChip(ctx, 388, drawStarIcon, t('hud.score'), String(state.score), C.accent);
  }
}

/** Draw one icon + label + value readout chip anchored at `x`. */
function statChip(
  ctx: CanvasRenderingContext2D,
  x: number,
  icon: (ctx: CanvasRenderingContext2D, cx: number, cy: number, color: string) => void,
  label: string,
  value: string,
  valueColor: string,
): void {
  icon(ctx, x + 11, 22, valueColor);
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.font = `9px ${FONT_MONO}`;
  ctx.fillStyle = C.hudMuted;
  ctx.fillText(spaced(label), x + 27, 15);
  ctx.font = `700 19px ${FONT_DISPLAY}`;
  ctx.save();
  ctx.fillStyle = valueColor;
  ctx.shadowBlur = 8;
  ctx.shadowColor = valueColor;
  ctx.fillText(value, x + 27, 34);
  ctx.restore();
}

/** Letter-space a short label for the diagnostics look. */
function spaced(s: string): string {
  return s.split('').join(' ');
}

/** A glowing coin glyph. */
function drawCoinIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number, color: string): void {
  ctx.save();
  ctx.shadowBlur = 7;
  ctx.shadowColor = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(cx, cy, 8, 0, Math.PI * 2);
  ctx.stroke();
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(cx, cy - 4.5);
  ctx.lineTo(cx, cy + 4.5);
  ctx.stroke();
  ctx.restore();
}

/** A hexagonal Core glyph. */
function drawCoreIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number, color: string): void {
  ctx.save();
  ctx.shadowBlur = 7;
  ctx.shadowColor = color;
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  for (let i = 0; i < 6; i += 1) {
    const a = -Math.PI / 2 + (Math.PI / 3) * i;
    const px = cx + Math.cos(a) * 8;
    const py = cy + Math.sin(a) * 8;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy, 2.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** A radar-sweep wave glyph. */
function drawWaveIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number, color: string): void {
  ctx.save();
  ctx.shadowBlur = 6;
  ctx.shadowColor = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  for (let i = 1; i <= 3; i += 1) {
    ctx.globalAlpha = 1 - (i - 1) * 0.28;
    ctx.beginPath();
    ctx.arc(cx - 5, cy + 3, i * 3.4, -Math.PI / 2.3, 0.15);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx - 5, cy + 3, 1.8, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** A four-point star glyph. */
function drawStarIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number, color: string): void {
  ctx.save();
  ctx.shadowBlur = 7;
  ctx.shadowColor = color;
  ctx.fillStyle = color;
  ctx.beginPath();
  const spikes = [8, 2.6, 8, 2.6];
  for (let i = 0; i < 8; i += 1) {
    const a = -Math.PI / 2 + (Math.PI / 4) * i;
    const r = spikes[i % 4]!;
    const px = cx + Math.cos(a) * r;
    const py = cy + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

// --- power grid meter --------------------------------------------------------

/** Row 1, right: the segmented power-grid meter. */
function drawPowerBar(ctx: CanvasRenderingContext2D, state: GameState, time: number): void {
  const { supply, demand, efficiency } = state.power;
  const brownout = efficiency < 1;
  const x = 540;
  // Stops short of the right edge to clear the `?` Help button.
  const w = 220;
  const barY = 21;
  const barH = 15;
  const accent = brownout ? C.warning : C.good;

  // Label + numeric readout.
  ctx.textBaseline = 'alphabetic';
  ctx.font = `9px ${FONT_MONO}`;
  ctx.textAlign = 'left';
  ctx.fillStyle = C.hudMuted;
  ctx.fillText(spaced(t('hud.powerGrid')), x, 14);
  ctx.textAlign = 'right';
  ctx.font = `11px ${FONT_MONO}`;
  ctx.save();
  if (brownout) {
    ctx.fillStyle = C.warning;
    ctx.shadowBlur = 8;
    ctx.shadowColor = C.warning;
  } else {
    ctx.fillStyle = C.hudText;
  }
  ctx.fillText(
    t('hud.powerReadout', { demand, supply, pct: Math.round(efficiency * 100) }),
    x + w,
    14,
  );
  ctx.restore();

  // Meter housing.
  ctx.fillStyle = '#06101f';
  roundRect(ctx, x, barY, w, barH, 3);
  ctx.fill();
  ctx.strokeStyle = withAlpha(accent, 0.45);
  ctx.lineWidth = 1;
  ctx.stroke();

  // Segmented fill: demand as a fraction of supply.
  const segs = 26;
  const gap = 2;
  const segW = (w - 4 - gap * (segs - 1)) / segs;
  const ratio = clamp(demand / Math.max(supply, 1), 0, 1);
  const lit = brownout ? segs : Math.round(ratio * segs);
  const pulse = 0.55 + 0.45 * Math.abs(Math.sin(time * 5));
  for (let i = 0; i < segs; i += 1) {
    const sx = x + 2 + i * (segW + gap);
    const on = i < lit;
    // Lit segments brighten toward the demand frontier for a "charge" look.
    ctx.fillStyle = on
      ? brownout
        ? withAlpha(C.warning, 0.55 + 0.45 * pulse)
        : withAlpha(accent, 0.5 + 0.5 * (i / Math.max(1, lit)))
      : 'rgba(80, 110, 150, 0.16)';
    ctx.fillRect(sx, barY + 2, segW, barH - 4);
  }

  // Supply tick marker: where current demand meets capacity.
  if (!brownout && demand > 0) {
    const markX = x + 2 + ratio * (w - 4);
    ctx.strokeStyle = C.hudText;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(markX, barY - 1);
    ctx.lineTo(markX, barY + barH + 1);
    ctx.stroke();
  }

  // Sprint 26 (REC 3, docs/REC3_DESIGN.md §4.3): the "OC ×N" pip — a small
  // brownout-orange caption just under the power-bar label when any tower is
  // Overclocked, telling the player at a glance how many towers are running
  // rich. Drawn flush-right above the meter so it never collides with the
  // numeric readout above. Hidden when `overclockCount === 0` so a no-
  // Overclock player sees the same HUD they had in Sprint 25.
  const n = state.power.overclockCount;
  if (n > 0) {
    const ocPulse = 0.55 + 0.45 * Math.abs(Math.sin(time * 4));
    ctx.save();
    ctx.font = `700 9px ${FONT_MONO}`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = withAlpha(C.warning, 0.6 + 0.4 * ocPulse);
    ctx.shadowBlur = 6;
    ctx.shadowColor = C.warning;
    ctx.fillText(t('hud.overclockPip', { n }), x + w, barY + barH + 11);
    ctx.restore();
  }
}

// --- command dock ------------------------------------------------------------

/** Per-ability signature accent (mirrors render.ts). */
const ABILITY_COLOR: Record<AbilityId, string> = {
  overcharge: '#2de2e6',
  airstrike: '#ff8c42',
  empBurst: '#bfe6ff',
};

/** Ability-slot hotkey caps, in `state.abilities` order. */
const ABILITY_KEYS: readonly string[] = ['Q', 'W', 'E'];

/**
 * Compact enemy name for the WAVE-zone incoming preview — resolved live
 * through `t('hud.spawnShort.<type>')` so a locale switch updates every
 * preview row without rebuild. The English defaults live in
 * `src/i18n/en.ts:hud.spawnShort` (Sprint 69, i18n-gaps-extract-s69).
 */
function spawnShort(type: SpawnType): string {
  return t(`hud.spawnShort.${type}`);
}

/**
 * Sprint 69 — i18n key suffix per Overload tier index (0..4). `CONFIG.over
 * loadTiers[i].name` carries the English fallback; the live UI calls
 * `t('overload.tierName.<key>')` to honour the active locale.
 */
const OVERLOAD_TIER_KEY: readonly string[] = [
  'nominal',
  'charged',
  'surging',
  'critical',
  'meltdown',
];

/** Resolve the localised name for Overload tier `i`, or '' on out-of-range. */
function overloadTierName(i: number): string {
  const k = OVERLOAD_TIER_KEY[i];
  return k ? t(`overload.tierName.${k}`).toUpperCase() : '';
}

/** Memo for the incoming-wave preview — `genWave` is pure but worth caching. */
let wavePreview: { key: string; def: WaveDef } | null = null;

/**
 * The composition of the *next* wave, with the selected difficulty + Overload
 * count multipliers folded in — so the build-phase preview is honest about
 * what the player is about to face (docs §8 / §4.3).
 */
function incomingWave(state: GameState): WaveDef {
  const w = state.wave + 1;
  const countMult =
    CONFIG.difficulties[state.difficulty].enemyCountMult *
    (CONFIG.overloadTiers[state.overload]?.countMult ?? 1);
  const key = `${state.seed}|${w}|${state.difficulty}|${state.overload}`;
  if (!wavePreview || wavePreview.key !== key) {
    wavePreview = { key, def: genWave(state.seed, w, countMult) };
  }
  return wavePreview.def;
}

/**
 * Tier 2 — the COMMAND DOCK: three framed, labelled zones (BUILD / WAVE /
 * ABILITIES) holding every interactive control. One contiguous band directly
 * above the playfield (§0 / §3).
 */
function drawCommandDock(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  profile: Profile,
  time: number,
): void {
  const layout = hudLayout(state.mode, profile);
  const waveActive = state.phase === 'wave';

  drawZoneFrame(ctx, layout.buildZone, t('hud.buildZone'));
  drawZoneFrame(ctx, layout.waveZone, t('hud.waveZone'));
  drawZoneFrame(ctx, layout.abilitiesZone, waveActive ? t('hud.abilitiesZone') : t('hud.overloadZone'));

  // BUILD — six tower chips, plus the Wall chip in Maze Mode (§16.9).
  for (const chip of layout.towerChips) drawTowerChip(ctx, chip, state);

  // WAVE — primary action, speed / mute toggles, incoming-wave preview.
  drawWaveZone(ctx, layout, state, time);

  // ABILITIES — Overload dial (build, wave ≥ 1) ⇄ ability slots (wave). The
  // two are time-exclusive and share the zone (§4.4).
  if (waveActive) {
    drawAbilitySlots(ctx, layout, state, time);
  } else if (state.phase === 'build' && state.wave >= 1) {
    drawOverloadDial(ctx, layout, state, time);
  } else {
    drawZoneIdle(ctx, layout.abilitiesZone);
  }
}

/** Draw one COMMAND DOCK zone — a framed sub-panel with a small caps label. */
function drawZoneFrame(ctx: CanvasRenderingContext2D, zone: Rect, label: string): void {
  ctx.save();
  const g = ctx.createLinearGradient(0, zone.y, 0, zone.y + zone.h);
  g.addColorStop(0, 'rgba(16, 24, 46, 0.92)');
  g.addColorStop(1, 'rgba(9, 14, 28, 0.92)');
  ctx.fillStyle = g;
  roundRect(ctx, zone.x, zone.y, zone.w, zone.h, 8);
  ctx.fill();
  ctx.strokeStyle = withAlpha(C.accent, 0.3);
  ctx.lineWidth = 1;
  roundRect(ctx, zone.x + 0.5, zone.y + 0.5, zone.w - 1, zone.h - 1, 8);
  ctx.stroke();
  ctx.font = `9px ${FONT_MONO}`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = C.hudMuted;
  ctx.fillText(spaced(label), zone.x + 9, zone.y + 13);
  ctx.restore();
}

/** A faint placeholder caption for the ABILITIES zone on the title / end screens. */
function drawZoneIdle(ctx: CanvasRenderingContext2D, zone: Rect): void {
  ctx.save();
  ctx.font = `8px ${FONT_MONO}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = withAlpha(C.hudMuted, 0.6);
  ctx.fillText(t('hud.overloadIdle'), zone.x + zone.w / 2, zone.y + zone.h / 2 + 6);
  ctx.restore();
}

/**
 * Sprint 63 (puzzle-ux-briefing-s63) — true when `chip` is a tower that is
 * **not allowed** by the active puzzle level. In Campaign / Endless / Maze
 * this always returns false (no puzzle constraints apply). In puzzle mode,
 * the level's `allowedTowers` whitelist is the gate; anything outside it is
 * effectively a locked tower for the duration of the attempt — the BUILD
 * chip is dimmed + lock-stamped so the player cannot try to place it.
 */
function isPuzzleForbiddenChip(chip: TowerChipRect, state: GameState): boolean {
  if (state.mode !== 'puzzle' || !state.puzzleLevelId) return false;
  const level = getPuzzleLevel(state.puzzleLevelId);
  if (!level) return false;
  return !level.allowedTowers.includes(chip.type);
}

/** One BUILD-zone tower chip — glyph, name, cost / power draw, hotkey cap. */
function drawTowerChip(
  ctx: CanvasRenderingContext2D,
  chip: TowerChipRect,
  state: GameState,
): void {
  const def = CONFIG.towers[chip.type];
  const r = chip.rect;
  // Sprint 63 — puzzle-mode forbidden chip reads as "locked for this
  // attempt" — same visual treatment as the §19.2 unlock-gated chip
  // (dimmed + lock glyph) so the player can't accidentally arm a tower
  // the level forbids. The OR with `chip.locked` keeps the existing
  // Flux-unlock dimming intact in puzzle mode (Puzzle towers are
  // unlock-free by Sprint 30 design, but the OR is defensive).
  const puzzleForbidden = isPuzzleForbiddenChip(chip, state);
  const locked = chip.locked || puzzleForbidden;
  // A locked tower can never be armed by the player (the unlock gate blocks
  // it), but guard regardless so a locked chip never reads as selected.
  const armed = !locked && state.selectedTowerType === chip.type;
  const affordable = state.gold >= def.cost;

  ctx.save();
  // A still-locked tower (§19.2) reads dim with a lock badge + Flux-cost
  // sub-line, mirroring the title-screen setup chips. Unaffordability dims too.
  //
  // Sprint 72 (ux-zorder-fix-s72) — Track C: the original blanket 0.5 alpha
  // on locked chips dimmed the *glyph* down to where the Sniper's narrow
  // muzzle-tip rail effectively vanished against the chip backdrop. A
  // tester reported "sniper does not have an icon" on P0 "First Light" —
  // the Sniper *is* the puzzle-disabled tower there, and the glyph was
  // unreadable. We split the dim into two passes:
  //  • backdropAlpha — the chip rect + border + hotkey cap (the *frame*)
  //    stays heavily dimmed so the chip still reads "off" at a glance;
  //  • foregroundAlpha — the glyph + name + lock badge + sub-line stay
  //    bright enough that the player can still tell which tower it is
  //    ("this is the Sniper, it's locked for this level").
  // Unaffordability is left at the original 0.4 blanket — it's a
  // transient gold gate, not an identity gate.
  const backdropAlpha = locked ? 0.45 : !affordable ? 0.4 : 1;
  const foregroundAlpha = locked ? 0.92 : !affordable ? 0.4 : 1;

  ctx.globalAlpha = backdropAlpha;
  if (armed) {
    const g = ctx.createLinearGradient(0, r.y, 0, r.y + r.h);
    g.addColorStop(0, withAlpha(def.color, 0.95));
    g.addColorStop(1, withAlpha(def.color, 0.55));
    ctx.fillStyle = g;
  } else {
    ctx.fillStyle = '#101a30';
  }
  roundRect(ctx, r.x, r.y, r.w, r.h, 5);
  ctx.fill();
  ctx.strokeStyle = def.color;
  ctx.lineWidth = armed ? 1.9 : 1.1;
  if (armed) {
    ctx.shadowBlur = 12;
    ctx.shadowColor = def.color;
  }
  roundRect(ctx, r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1, 5);
  ctx.stroke();
  ctx.shadowBlur = 0;

  const ink = armed ? '#06101f' : C.hudText;

  // Hotkey digit cap (top-left corner) — the Wall is hotkey 7 (§16.9), past
  // the six core chips, so it is not in TOWER_ORDER. Sprint 72 — kept at the
  // backdrop alpha (chip is locked-off, hotkey doesn't fire either).
  const key = chip.type === 'wall' ? '7' : String(TOWER_ORDER.indexOf(chip.type) + 1);
  ctx.fillStyle = armed ? withAlpha('#06101f', 0.28) : withAlpha(def.color, 0.22);
  roundRect(ctx, r.x + 4, r.y + 4, 12, 12, 3);
  ctx.fill();
  ctx.fillStyle = armed ? '#06101f' : def.color;
  ctx.font = `700 9px ${FONT_DISPLAY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(key, r.x + 10, r.y + 11);

  // Sprint 72 — switch to the foreground alpha so the tower's *identity*
  // (glyph + name + lock badge + sub-line) reads clearly even on a locked
  // chip. Player still sees "this is the Sniper" at a glance.
  ctx.globalAlpha = foregroundAlpha;

  // Tower glyph.
  drawTowerGlyph(ctx, r.x + r.w / 2, r.y + 20, chip.type, armed ? '#06101f' : def.color);

  // Name.
  ctx.fillStyle = ink;
  ctx.font = `700 8px ${FONT_DISPLAY}`;
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(towerShort(chip.type), r.x + r.w / 2, r.y + 35);

  // Lock badge (top-right) — the at-a-glance marker that this tower is gated.
  if (locked) drawLockGlyph(ctx, r.x + r.w - 11, r.y + 11, C.warning);

  // Sub-line — a locked tower shows its Flux unlock cost instead of the build
  // cost, so the player sees the unlock path before clicking. The Wall is
  // non-weapon but supplies nothing — it just reports its 0 power draw, so it
  // never reads as a Pylon (§16.9). Sprint 63 — a puzzle-forbidden chip
  // shows the literal LOCKED tag without a Flux cost (puzzle locks are per-
  // attempt, not metaprogression — there is nothing for the player to buy).
  let sub: string;
  if (puzzleForbidden) sub = t('hud.locked');
  else if (locked) sub = `${t('hud.locked')} ✦${towerUnlockCost(chip.type)}`;
  else if (def.weapon)
    sub = t('hud.towerCostDraw', { cost: def.cost, power: def.levels[0].powerDraw });
  else if (chip.type === 'pylon')
    sub = t('hud.pylonCostSupply', {
      cost: def.cost,
      supply: CONFIG.towers.pylon.levels[0].supply,
    });
  else sub = t('hud.wallCost', { cost: def.cost });
  ctx.font = `7px ${FONT_MONO}`;
  ctx.fillStyle = locked ? C.warning : armed ? withAlpha('#06101f', 0.78) : C.hudMuted;
  ctx.fillText(sub, r.x + r.w / 2, r.y + 44);
  ctx.restore();
}

/** WAVE zone — the primary action button, two toggles and a wave preview. */
function drawWaveZone(
  ctx: CanvasRenderingContext2D,
  layout: HudLayout,
  state: GameState,
  time: number,
): void {
  // Primary action — START / NEXT WAVE (build), PAUSE / RESUME (wave),
  // RESTART (won / lost). The locked-in Overload tier tints the frame.
  let title = t('hud.restart');
  let sub = t('hud.rebootHint');
  let color = C.good;
  if (state.phase === 'build') {
    title = state.wave === 0 ? t('hud.startWave') : t('hud.nextWave');
    sub = t('hud.deployHint');
    const tier = CONFIG.overloadTiers[state.overload];
    if (state.overload > 0 && tier) {
      sub = t('hud.startWaveOverload', {
        n: state.overload,
        tierName: overloadTierName(state.overload),
      });
      color = OVERLOAD_RAMP[state.overload] ?? C.warning;
    }
  } else if (state.phase === 'wave') {
    title = state.paused ? t('hud.resume') : t('hud.pause');
    sub = t('hud.holdHint');
  }
  drawDockButton(ctx, layout.start, title, sub, color, time);

  // Secondary toggles — deliberately the smallest dock targets (§4.3).
  drawSmallToggle(ctx, layout.speed, `${state.speed}×`, state.speed === 2);
  drawSmallToggle(ctx, layout.mute, state.muted ? t('hud.audioOff') : t('hud.audioOn'), !state.muted);

  // Incoming-wave preview — build phase only.
  if (state.phase === 'build') drawIncomingPreview(ctx, layout.waveZone, state);
}

/** The WAVE zone's primary action button — the largest dock target (§1.4). */
function drawDockButton(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  title: string,
  sub: string,
  color: string,
  time: number,
): void {
  const pulse = 0.6 + 0.4 * Math.sin(time * 3);
  ctx.save();
  const g = ctx.createLinearGradient(0, r.y, 0, r.y + r.h);
  g.addColorStop(0, withAlpha(color, 0.92));
  g.addColorStop(1, withAlpha(color, 0.55));
  ctx.fillStyle = g;
  roundRect(ctx, r.x, r.y, r.w, r.h, 6);
  ctx.fill();
  ctx.strokeStyle = withAlpha(color, 0.7 + 0.3 * pulse);
  ctx.lineWidth = 1.7;
  ctx.shadowBlur = 10;
  ctx.shadowColor = color;
  roundRect(ctx, r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1, 6);
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#06101f';
  ctx.font = `700 14px ${FONT_DISPLAY}`;
  ctx.fillText(title, r.x + r.w / 2, r.y + r.h / 2 - 5);
  ctx.font = `8px ${FONT_MONO}`;
  ctx.fillStyle = withAlpha('#06101f', 0.82);
  ctx.fillText(sub, r.x + r.w / 2, r.y + r.h / 2 + 9);
  ctx.restore();
}

/** A small WAVE-zone secondary toggle (speed / mute). */
function drawSmallToggle(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  label: string,
  active: boolean,
): void {
  ctx.save();
  ctx.fillStyle = active ? withAlpha(C.accent, 0.85) : '#101a30';
  roundRect(ctx, r.x, r.y, r.w, r.h, 4);
  ctx.fill();
  ctx.strokeStyle = withAlpha(C.accent, active ? 0.9 : 0.4);
  ctx.lineWidth = 1;
  roundRect(ctx, r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1, 4);
  ctx.stroke();
  ctx.fillStyle = active ? '#06101f' : C.hudMuted;
  ctx.font = `700 8px ${FONT_DISPLAY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2 + 0.5);
  ctx.restore();
}

/** One-line incoming-wave preview at the foot of the WAVE zone (§4.3). */
function drawIncomingPreview(
  ctx: CanvasRenderingContext2D,
  zone: Rect,
  state: GameState,
): void {
  const wave = incomingWave(state);
  const counts = new Map<SpawnType, number>();
  for (const grp of wave.groups) {
    counts.set(grp.type, (counts.get(grp.type) ?? 0) + grp.count);
  }
  ctx.save();
  ctx.font = `8px ${FONT_MONO}`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = C.hudMuted;
  const maxW = zone.w - 14;
  let text = '▸';
  for (const [type, n] of counts) {
    const next = `${text} ${spawnShort(type)}×${n}`;
    if (text !== '▸' && ctx.measureText(`${next} …`).width > maxW) {
      text = `${text} …`;
      break;
    }
    text = next;
  }
  // Sprint 47: Endless per-wave modifier badge. The composition row drops down
  // 8 px so the badge sits above it, on the same line where the player is
  // already deciding (build-phase preview).
  const modifier = wave.modifier;
  if (modifier !== undefined) {
    const def = ENDLESS_MODIFIERS.find((m) => m.id === modifier);
    if (def !== undefined) {
      ctx.fillStyle = C.accent;
      // Sprint 69 — modifier label routes through `t('endlessMod.{id}.label')`
      // so the badge reads in the player's language.
      ctx.fillText(
        t('hud.endlessModBadge', { label: t(`endlessMod.${def.id}.label`) }),
        zone.x + 7,
        zone.y + 44,
      );
      ctx.fillStyle = C.hudMuted;
    }
  }
  // Drawn between the primary button and the toggle row (§4.3).
  ctx.fillText(text, zone.x + 7, zone.y + 52);
  ctx.restore();
}

// --- ABILITIES zone: build-phase Overload dial -------------------------------

/**
 * Build-phase Overload dial — the 0–4 wave-intensity dial (docs §8), in the
 * ABILITIES zone. `−` / `+` steppers (keys `[` / `]`), five clickable tier
 * segments, the tier name, and a compact one-line multiplier readout.
 */
function drawOverloadDial(
  ctx: CanvasRenderingContext2D,
  layout: HudLayout,
  state: GameState,
  time: number,
): void {
  const zone = layout.abilitiesZone;
  const dial = layout.overload;
  const maxTier = CONFIG.overloadTiers.length - 1;
  const tierIdx = clamp(state.overload, 0, maxTier);
  const tier = CONFIG.overloadTiers[tierIdx]!;
  const accent = OVERLOAD_RAMP[tierIdx] ?? C.accent;
  const pulse = 0.5 + 0.5 * Math.sin(time * 3);

  ctx.save();
  // "NEXT WAVE" hint, right of the zone label.
  // Sprint 69 (i18n-gaps-extract-s69) — reuse-bug fix: the literal was
  // duplicating `hud.nextWaveHint` which already existed in the catalogue.
  ctx.font = `8px ${FONT_MONO}`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = withAlpha(accent, 0.8);
  ctx.fillText(t('hud.nextWaveHint'), zone.x + zone.w - 9, zone.y + 13);

  // Stepper buttons + their hotkey caps.
  drawOverloadStepper(ctx, dial.minus, '−', '[', accent, pulse);
  drawOverloadStepper(ctx, dial.plus, '+', ']', accent, pulse);

  // Tier segments — lit up to the selected tier, ramping cyan → red.
  dial.segments.forEach((seg, i) => {
    const lit = i <= tierIdx;
    const segColor = OVERLOAD_RAMP[i] ?? C.accent;
    const current = i === tierIdx;
    ctx.fillStyle = lit
      ? withAlpha(segColor, current ? 0.95 : 0.55)
      : 'rgba(40, 58, 92, 0.4)';
    roundRect(ctx, seg.x, seg.y, seg.w, seg.h, 3);
    ctx.fill();
    ctx.strokeStyle = current
      ? withAlpha(segColor, 0.7 + 0.3 * pulse)
      : withAlpha(lit ? segColor : '#5a82be', 0.5);
    ctx.lineWidth = current ? 1.8 : 1;
    roundRect(ctx, seg.x + 0.5, seg.y + 0.5, seg.w - 1, seg.h - 1, 3);
    ctx.stroke();
    ctx.fillStyle = current ? '#07101e' : lit ? '#08121f' : C.hudMuted;
    ctx.font = `700 11px ${FONT_DISPLAY}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(i), seg.x + seg.w / 2, seg.y + seg.h / 2 + 0.5);
  });

  // Tier name. Sprint 69 — reuse-bug fix: `hud.overloadTier` existed but
  // the renderer was building the string by hand.
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = `700 11px ${FONT_DISPLAY}`;
  ctx.fillStyle = accent;
  ctx.fillText(
    t('hud.overloadTier', { tier: tierIdx, name: overloadTierName(tierIdx) }),
    zone.x + 9,
    zone.y + 54,
  );

  // One-line multiplier readout — threat trio neutral, reward pair green.
  ctx.font = `8px ${FONT_MONO}`;
  let mx = zone.x + 9;
  const my = zone.y + 65;
  const mult = (label: string, val: string, color: string): void => {
    ctx.fillStyle = C.hudMuted;
    ctx.fillText(label, mx, my);
    mx += ctx.measureText(label).width + 1;
    ctx.fillStyle = color;
    ctx.fillText(val, mx, my);
    mx += ctx.measureText(val).width + 5;
  };
  mult(t('hud.tierMult.hp'), tier.hpMult.toFixed(2), C.hudText);
  mult(t('hud.tierMult.sp'), tier.speedMult.toFixed(2), C.hudText);
  mult(t('hud.tierMult.cn'), tier.countMult.toFixed(2), C.hudText);
  mult(t('hud.tierMult.gd'), tier.goldMult.toFixed(2), C.good);
  mult(t('hud.tierMult.sc'), tier.scoreMult.toFixed(2), C.good);
  ctx.restore();
}

/** One Overload `−` / `+` stepper, captioned with its hotkey. */
function drawOverloadStepper(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  symbol: string,
  key: string,
  accent: string,
  pulse: number,
): void {
  ctx.save();
  ctx.fillStyle = '#101a30';
  roundRect(ctx, r.x, r.y, r.w, r.h, 4);
  ctx.fill();
  ctx.strokeStyle = withAlpha(accent, 0.6 + 0.2 * pulse);
  ctx.lineWidth = 1.1;
  roundRect(ctx, r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1, 4);
  ctx.stroke();
  ctx.fillStyle = accent;
  ctx.font = `700 13px ${FONT_DISPLAY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(symbol, r.x + r.w / 2, r.y + r.h / 2 + 0.5);
  // Hotkey cap below the stepper.
  ctx.fillStyle = C.hudMuted;
  ctx.font = `8px ${FONT_MONO}`;
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(key, r.x + r.w / 2, r.y + r.h + 9);
  ctx.restore();
}

// --- ABILITIES zone: wave-phase ability slots --------------------------------

/** Wave-phase ability slots — three slots (Q / W / E) in the ABILITIES zone. */
function drawAbilitySlots(
  ctx: CanvasRenderingContext2D,
  layout: HudLayout,
  state: GameState,
  time: number,
): void {
  // The locked Overload tier for the wave in progress — a small zone caption.
  if (state.waveOverload > 0) {
    const ti = clamp(state.waveOverload, 0, CONFIG.overloadTiers.length - 1);
    const oc = OVERLOAD_RAMP[ti] ?? C.warning;
    const name = overloadTierName(ti);
    ctx.save();
    ctx.font = `8px ${FONT_MONO}`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = oc;
    ctx.fillText(
      t('hud.waveOverloadPip', { n: ti, tierName: name }),
      layout.abilitiesZone.x + layout.abilitiesZone.w - 9,
      layout.abilitiesZone.y + 13,
    );
    ctx.restore();
  }
  state.abilities.forEach((a, i) => {
    const slot = layout.abilitySlots[i];
    if (slot) drawAbilitySlot(ctx, slot, a, ABILITY_KEYS[i] ?? '', state, time);
  });
}

/** Draw one ability slot: glyph, key cap, gold cost and radial cooldown sweep. */
function drawAbilitySlot(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  ability: AbilityRuntime,
  key: string,
  state: GameState,
  time: number,
): void {
  const color = ABILITY_COLOR[ability.id] ?? C.accent;
  const onCd = ability.cooldownLeft > 0;
  const affordable = state.gold >= ability.cost;
  const usable = !onCd && affordable;
  const active = ability.id === 'overcharge' && state.overchargeTicksLeft > 0;
  const lit = usable || active;
  const pulse = 0.5 + 0.5 * Math.sin(time * 6);

  ctx.save();
  // Slot body.
  const g = ctx.createLinearGradient(0, r.y, 0, r.y + r.h);
  g.addColorStop(0, usable ? 'rgba(14, 26, 40, 0.95)' : 'rgba(9, 13, 25, 0.92)');
  g.addColorStop(1, 'rgba(7, 10, 20, 0.95)');
  ctx.fillStyle = g;
  roundRect(ctx, r.x, r.y, r.w, r.h, 6);
  ctx.fill();
  ctx.strokeStyle = lit
    ? withAlpha(color, 0.7 + 0.3 * (active ? pulse : 1))
    : withAlpha('#3a4f74', 0.8);
  ctx.lineWidth = lit ? 1.6 : 1;
  if (lit) {
    ctx.shadowBlur = active ? 10 + 5 * pulse : 7;
    ctx.shadowColor = color;
  }
  roundRect(ctx, r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1, 6);
  ctx.stroke();
  ctx.shadowBlur = 0;

  // Key cap (top-left).
  const kc = 12;
  ctx.fillStyle = lit ? withAlpha(color, 0.92) : 'rgba(58, 79, 116, 0.6)';
  roundRect(ctx, r.x + 5, r.y + 5, kc, kc, 3);
  ctx.fill();
  ctx.fillStyle = '#07101e';
  ctx.font = `700 9px ${FONT_DISPLAY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(key, r.x + 5 + kc / 2, r.y + 5 + kc / 2 + 0.5);

  // Glyph.
  const gx = r.x + r.w / 2;
  const gy = r.y + 22;
  const gr = 10;
  const dim = !usable && !active;
  ctx.globalAlpha = dim ? 0.45 : 1;
  drawAbilityGlyph(ctx, gx, gy, ability.id, dim ? '#7d8bab' : color);
  ctx.globalAlpha = 1;

  // Radial cooldown sweep over the glyph.
  if (onCd) {
    const frac = clamp(ability.cooldownLeft / ability.cooldownTicks, 0, 1);
    ctx.fillStyle = 'rgba(6, 10, 20, 0.72)';
    ctx.beginPath();
    ctx.moveTo(gx, gy);
    ctx.arc(gx, gy, gr + 4, -Math.PI / 2, -Math.PI / 2 + frac * Math.PI * 2);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = withAlpha(color, 0.6);
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(gx, gy, gr + 4, -Math.PI / 2, -Math.PI / 2 + frac * Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = C.hudText;
    ctx.font = `700 10px ${FONT_DISPLAY}`;
    ctx.fillText(`${Math.ceil(ability.cooldownLeft / CONFIG.tickRate)}`, gx, gy + 0.5);
  }

  // Name. Sprint 69 — route through `abilities.{id}.label` so the slot
  // caption reads in the player's language (the runtime `ability.name`
  // is the English fallback from CONFIG).
  ctx.font = `700 8px ${FONT_DISPLAY}`;
  ctx.fillStyle = lit ? C.hudText : C.hudMuted;
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(t(`abilities.${ability.id}.label`).toUpperCase(), gx, r.y + 39);

  // Status line — cost, or ◉ ACTIVE while Overcharge runs.
  if (active) {
    ctx.fillStyle = withAlpha(color, 0.7 + 0.3 * pulse);
    ctx.font = `700 8px ${FONT_MONO}`;
    ctx.fillText(
      t('hud.abilityActive', { sec: Math.ceil(state.overchargeTicksLeft / CONFIG.tickRate) }),
      gx,
      r.y + 48,
    );
  } else {
    ctx.fillStyle = affordable ? '#f7d51d' : C.bad;
    ctx.font = `8px ${FONT_MONO}`;
    ctx.fillText(t('hud.costSuffix', { cost: ability.cost }), gx, r.y + 48);
  }
  ctx.restore();
}

/** A neon glyph for one ability: a bolt, a reticle or a static burst. */
function drawAbilityGlyph(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  id: AbilityId,
  color: string,
): void {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  if (id === 'overcharge') {
    const bolt: ReadonlyArray<readonly [number, number]> = [
      [1.5, -9],
      [-5, 1.5],
      [-1, 1.5],
      [-1.5, 9],
      [5, -1.5],
      [1, -1.5],
    ];
    ctx.beginPath();
    bolt.forEach(([bx, by], i) => (i === 0 ? ctx.moveTo(bx, by) : ctx.lineTo(bx, by)));
    ctx.closePath();
    ctx.fill();
  } else if (id === 'airstrike') {
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.arc(0, 0, 6.4, 0, Math.PI * 2);
    ctx.stroke();
    ctx.lineWidth = 1.8;
    for (let i = 0; i < 4; i += 1) {
      const a = (Math.PI / 2) * i;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * 3.2, Math.sin(a) * 3.2);
      ctx.lineTo(Math.cos(a) * 9.5, Math.sin(a) * 9.5);
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.arc(0, 0, 1.7, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.beginPath();
    ctx.arc(0, 0, 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.lineWidth = 1.5;
    for (let ring = 0; ring < 2; ring += 1) {
      const rr = 4.6 + ring * 3.6;
      for (let s = 0; s < 3; s += 1) {
        const a0 = (Math.PI * 2 * s) / 3 + ring * 0.5;
        ctx.beginPath();
        ctx.arc(0, 0, rr, a0, a0 + Math.PI / 2.4);
        ctx.stroke();
      }
    }
    for (let i = 0; i < 4; i += 1) {
      const a = (Math.PI / 2) * i + Math.PI / 4;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * 2.6, Math.sin(a) * 2.6);
      ctx.lineTo(Math.cos(a) * 9.6, Math.sin(a) * 9.6);
      ctx.stroke();
    }
  }
  ctx.restore();
}

// --- contextual selection panel ----------------------------------------------

/** Selection-panel geometry constants. */
const SEL_W = 176;
const SEL_PAD_X = 8;
const SEL_PAD_Y = 8;
const SEL_HEADER_H = 14;
const SEL_ROW_H = 18;
const SEL_ROW_GAP = 4;

/** A clickable action row inside the contextual selection panel. */
interface SelectionAction {
  // Sprint 26 (REC 3, docs/REC3_DESIGN.md §4.3): `overclock` is the per-tower
  // toggle — weapon towers only, never on Pylon / Wall.
  id: 'upgrade' | 'sell' | 'target' | 'overclock';
  rect: Rect;
}

/** Resolved layout of the contextual selection panel, anchored to its tower. */
interface SelectionPanelLayout {
  panel: Rect;
  actions: SelectionAction[];
  /** True when the panel was flipped *below* the tower (top-rows clamp). */
  flip: boolean;
}

/** The live tower behind `state.selectedTowerId`, or undefined. */
function selectedTower(state: GameState): Tower | undefined {
  if (state.selectedTowerId === null) return undefined;
  return state.towers.find((t) => t.id === state.selectedTowerId);
}

/**
 * Resolve the contextual selection panel's geometry — anchored to the selected
 * tower's pixel position, flipped below it for the top rows so it never
 * collides with the HUD strip, and clamped within the canvas (§5.2).
 */
function selectionPanelLayout(state: GameState): SelectionPanelLayout | null {
  const tower = selectedTower(state);
  if (!tower) return null;
  // The Wall is inert: no upgrade, no targeting — only Sell (§16.9). Weapon
  // towers add a targeting row PLUS the Sprint-26 per-tower Overclock chip
  // (REC 3, docs/REC3_DESIGN.md §4.3); the Pylon keeps Upgrade + Sell.
  const ids: SelectionAction['id'][] =
    tower.type === 'wall'
      ? ['sell']
      : CONFIG.towers[tower.type].weapon
        ? ['upgrade', 'sell', 'target', 'overclock']
        : ['upgrade', 'sell'];
  const rows = ids.length;
  const h = SEL_PAD_Y + SEL_HEADER_H + 4 + rows * SEL_ROW_H + (rows - 1) * SEL_ROW_GAP + SEL_PAD_Y;

  const cx = tower.col * TS + TS / 2;
  const towerTop = OY + tower.row * TS;
  const towerBot = OY + (tower.row + 1) * TS;
  // Default above; flip below for the top rows where it would hit the HUD.
  const flip = towerTop - 8 - h < CONFIG.hudHeight;
  const y = flip ? towerBot + 8 : towerTop - 8 - h;
  const x = clamp(cx - SEL_W / 2, 4, CONFIG.canvas.width - SEL_W - 4);
  const panel: Rect = { x, y, w: SEL_W, h };

  const actions: SelectionAction[] = [];
  let rowY = y + SEL_PAD_Y + SEL_HEADER_H + 4;
  for (const id of ids) {
    actions.push({
      id,
      rect: { x: x + SEL_PAD_X, y: rowY, w: SEL_W - 2 * SEL_PAD_X, h: SEL_ROW_H },
    });
    rowY += SEL_ROW_H + SEL_ROW_GAP;
  }
  return { panel, actions, flip };
}

/**
 * Which contextual selection-panel action sits under (x, y), or null. The
 * panel exists only while a tower is selected; `Game` hit-tests this before
 * playfield tile routing so a panel click never falls through (§5.4).
 */
export function selectionPanelButtonAt(
  x: number,
  y: number,
  state: GameState,
): 'upgrade' | 'sell' | 'target' | 'overclock' | null {
  const layout = selectionPanelLayout(state);
  if (!layout) return null;
  for (const a of layout.actions) {
    if (inRect(a.rect, x, y)) return a.id;
  }
  return null;
}

/**
 * The contextual selection panel — Upgrade / Sell / Targeting, shown only
 * while a tower is selected, anchored next to that tower on the playfield
 * (progressive, contextual disclosure — §1.5 / §5).
 */
function drawSelectionPanel(ctx: CanvasRenderingContext2D, state: GameState): void {
  const layout = selectionPanelLayout(state);
  if (!layout) return;
  const tower = selectedTower(state)!;
  const def = CONFIG.towers[tower.type];
  const { panel } = layout;

  ctx.save();
  // Connector notch pointing at the tower.
  const notchX = clamp(tower.col * TS + TS / 2, panel.x + 16, panel.x + panel.w - 16);
  ctx.fillStyle = 'rgba(11, 17, 33, 0.97)';
  ctx.beginPath();
  if (layout.flip) {
    ctx.moveTo(notchX - 7, panel.y);
    ctx.lineTo(notchX + 7, panel.y);
    ctx.lineTo(notchX, panel.y - 7);
  } else {
    ctx.moveTo(notchX - 7, panel.y + panel.h);
    ctx.lineTo(notchX + 7, panel.y + panel.h);
    ctx.lineTo(notchX, panel.y + panel.h + 7);
  }
  ctx.closePath();
  ctx.fill();

  // Panel body.
  const g = ctx.createLinearGradient(0, panel.y, 0, panel.y + panel.h);
  g.addColorStop(0, 'rgba(15, 23, 44, 0.97)');
  g.addColorStop(1, 'rgba(9, 14, 28, 0.97)');
  ctx.fillStyle = g;
  roundRect(ctx, panel.x, panel.y, panel.w, panel.h, 8);
  ctx.fill();
  ctx.strokeStyle = withAlpha(def.color, 0.75);
  ctx.lineWidth = 1.3;
  ctx.shadowBlur = 10;
  ctx.shadowColor = withAlpha(def.color, 0.6);
  roundRect(ctx, panel.x, panel.y, panel.w, panel.h, 8);
  ctx.stroke();
  ctx.shadowBlur = 0;

  // Header — tower name + level.
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = `700 10px ${FONT_DISPLAY}`;
  ctx.fillStyle = def.color;
  // The Wall has no levels, so its header omits the "· L1" suffix (§16.9).
  // Sprint 69 — read tower label through `t('towers.{type}.label')` so the
  // selection panel reads in the player's language (was reading the English
  // CONFIG.towers[*].label only).
  const towerLabel = t(`towers.${tower.type}.label`).toUpperCase();
  const header = tower.type === 'wall' ? towerLabel : `${towerLabel} · L${tower.level}`;
  ctx.fillText(header, panel.x + SEL_PAD_X, panel.y + SEL_PAD_Y + 10);

  for (const action of layout.actions) drawSelectionAction(ctx, action, tower, state);
  ctx.restore();
}

/** One action row of the contextual selection panel — keeps its hotkey hint. */
function drawSelectionAction(
  ctx: CanvasRenderingContext2D,
  action: SelectionAction,
  tower: Tower,
  state: GameState,
): void {
  const r = action.rect;
  let title: string;
  let sub: string;
  let color: string;
  let enabled = true;
  if (action.id === 'upgrade') {
    color = '#f7d51d';
    if (tower.upgradeCost === null) {
      title = t('hud.maxLevel');
      sub = `L${tower.level}`;
      enabled = false;
    } else {
      title = t('hud.upgrade', { level: tower.level + 1 });
      sub = t('hud.upgradeHint', { cost: tower.upgradeCost });
      enabled = state.gold >= tower.upgradeCost;
    }
  } else if (action.id === 'sell') {
    color = C.warning;
    title = t('hud.sell');
    sub = t('hud.sellHint', { refund: Math.floor(tower.totalSpent * CONFIG.sellRefundRate) });
  } else if (action.id === 'target') {
    color = C.accent;
    title = t('hud.target', { mode: tower.targetMode.toUpperCase() });
    sub = t('hud.cycleTarget');
  } else {
    // Sprint 26 (REC 3, docs/REC3_DESIGN.md §4.3): the OVERCLOCK chip — when
    // ON it pulses the same `warning` orange the brownout power-bar uses, so
    // the *cost* (extra draw) is the same colour the player already reads as
    // "watch the grid." The sub-line shows the live multiplier pair as a
    // standing reminder of the burst-vs-cost trade.
    color = C.warning;
    title = tower.overclock ? t('hud.overclockOn') : t('hud.overclock');
    sub = `×${CONFIG.overclockRateMult.toFixed(1)}fr / ×${CONFIG.overclockDrawMult.toFixed(2)}w · [O]`;
  }

  // Sprint 26 (REC 3) — the OVERCLOCK row reads as "ON" with a filled body
  // when its toggle is live, so the chip carries the at-a-glance state the
  // tower-side halo + HUD pip reinforce.
  const active = action.id === 'overclock' && tower.overclock;
  ctx.save();
  if (!enabled) ctx.globalAlpha = 0.4;
  ctx.fillStyle = active ? withAlpha(color, 0.55) : '#101a30';
  roundRect(ctx, r.x, r.y, r.w, r.h, 4);
  ctx.fill();
  ctx.strokeStyle = withAlpha(color, active ? 0.95 : 0.6);
  ctx.lineWidth = active ? 1.6 : 1;
  roundRect(ctx, r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1, 4);
  ctx.stroke();
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.font = `700 9px ${FONT_DISPLAY}`;
  ctx.fillStyle = active ? '#06101f' : color;
  ctx.fillText(title, r.x + 8, r.y + r.h / 2 + 0.5);
  ctx.textAlign = 'right';
  ctx.font = `8px ${FONT_MONO}`;
  ctx.fillStyle = active ? withAlpha('#06101f', 0.82) : C.hudMuted;
  ctx.fillText(sub, r.x + r.w - 8, r.y + r.h / 2 + 0.5);
  ctx.restore();
}

/**
 * A small padlock badge — marks a BUILD-zone tower chip that is still
 * unlock-gated for the profile (§19.2). Drawn purely with canvas primitives so
 * it carries no font dependency.
 */
function drawLockGlyph(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  color: string,
): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  // Shackle — an arc over the top of the body (canvas y grows downward, so the
  // upper semicircle runs from π through 2π).
  ctx.lineWidth = 1.3;
  ctx.beginPath();
  ctx.arc(cx, cy - 0.6, 2.3, Math.PI, Math.PI * 2);
  ctx.stroke();
  // Body.
  roundRect(ctx, cx - 3.4, cy - 0.6, 6.8, 5.4, 1.1);
  ctx.fill();
  // Keyhole punched into the body.
  ctx.fillStyle = withAlpha('#06101f', 0.85);
  ctx.beginPath();
  ctx.arc(cx, cy + 1.8, 0.85, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** A miniature tower silhouette for a button. */
function drawTowerGlyph(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  type: TowerType,
  color: string,
): void {
  ctx.save();
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  if (type === 'pulse') {
    ctx.beginPath();
    ctx.arc(cx, cy, 5.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillRect(cx - 1.5, cy - 9, 3, 6);
  } else if (type === 'arc') {
    ctx.beginPath();
    ctx.moveTo(cx, cy - 6.5);
    ctx.lineTo(cx + 6, cy + 5);
    ctx.lineTo(cx - 6, cy + 5);
    ctx.closePath();
    ctx.fill();
  } else if (type === 'cryo') {
    ctx.lineWidth = 1.6;
    ctx.lineCap = 'round';
    for (let i = 0; i < 6; i += 1) {
      const a = (Math.PI / 3) * i;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(a) * 6.5, cy + Math.sin(a) * 6.5);
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.arc(cx, cy, 2, 0, Math.PI * 2);
    ctx.fill();
  } else if (type === 'mortar') {
    ctx.beginPath();
    ctx.moveTo(cx - 4, cy - 5);
    ctx.lineTo(cx + 4, cy - 5);
    ctx.lineTo(cx + 7, cy + 5);
    ctx.lineTo(cx - 7, cy + 5);
    ctx.closePath();
    ctx.fill();
  } else if (type === 'sniper') {
    // Long narrow rail with a breech block and a bright muzzle tip.
    ctx.save();
    ctx.rotate(-Math.PI / 5);
    roundRect(ctx, cx - 8, cy - 1.4, 16, 2.8, 1.2);
    ctx.fill();
    ctx.fillRect(cx - 8.5, cy - 3.4, 4, 6.8);
    ctx.restore();
  } else if (type === 'wall') {
    // A stout structural block with a brick seam — clearly inert, not a gun.
    roundRect(ctx, cx - 6.5, cy - 5.5, 13, 11, 1.8);
    ctx.fill();
    ctx.strokeStyle = withAlpha('#06101f', 0.55);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cx - 6.5, cy);
    ctx.lineTo(cx + 6.5, cy);
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.moveTo(cx, cy - 7);
    ctx.lineTo(cx + 7, cy);
    ctx.lineTo(cx, cy + 7);
    ctx.lineTo(cx - 7, cy);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

// --- notice ------------------------------------------------------------------

/** Transient message pill, drawn just below the HUD strip. */
function drawNotice(ctx: CanvasRenderingContext2D, state: GameState): void {
  if (!state.notice) return;
  const text = state.notice.text.toUpperCase();
  const fade = clamp(state.notice.ttl / 8, 0, 1);
  ctx.save();
  ctx.globalAlpha = fade;
  ctx.font = `700 12px ${FONT_MONO}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const w = ctx.measureText(text).width + 32;
  const cx = CONFIG.canvas.width / 2;
  const y = CONFIG.hudHeight + 14;
  ctx.fillStyle = 'rgba(8, 12, 24, 0.92)';
  roundRect(ctx, cx - w / 2, y - 13, w, 26, 13);
  ctx.fill();
  ctx.strokeStyle = C.accent;
  ctx.lineWidth = 1.2;
  ctx.shadowBlur = 10;
  ctx.shadowColor = C.accent;
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.fillStyle = C.hudText;
  ctx.fillText(text, cx, y + 1);
  ctx.restore();
}

// --- overlays ----------------------------------------------------------------

/** Full-canvas overlay for paused / won / lost. */
function drawOverlay(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  profile: Profile,
  time: number,
): void {
  const paused = state.phase === 'wave' && state.paused;
  if (state.phase !== 'won' && state.phase !== 'lost' && !paused) return;
  // Sprint 63 (puzzle-ux-briefing-s63) — puzzle mode renders its own
  // Solved / Try-again overlay (see `src/ui/puzzleSelect.ts:drawPuzzleResult`
  // and the orchestrator's `isPuzzleResultOpen()` gate). Skip the legacy
  // HUD overlay for puzzle won / lost so the two never stack.
  if (state.mode === 'puzzle' && (state.phase === 'won' || state.phase === 'lost')) {
    return;
  }

  const { width, height } = CONFIG.canvas;
  ctx.save();
  ctx.fillStyle = 'rgba(5, 8, 16, 0.86)';
  ctx.fillRect(0, 0, width, height);
  const cx = width / 2;
  const cy = height / 2;

  if (paused) {
    drawPanel(ctx, cx, cy, 320, 150, C.accent, time);
    centeredText(ctx, t('overlay.paused'), cx, cy - 18, `700 40px ${FONT_DISPLAY}`, C.accent, 18);
    centeredText(ctx, t('overlay.pauseHint'), cx, cy + 28, `12px ${FONT_MONO}`, C.hudMuted, 0);
    ctx.restore();
    return;
  }

  const pulse = 0.6 + 0.4 * Math.sin(time * 3);

  if (state.phase === 'won') {
    // Campaign victory — taller panel, with a Continue → Endless button.
    drawPanel(ctx, cx, cy, 468, 372, C.good, time);
    const top = cy - 156;
    centeredText(ctx, t('overlay.missionComplete'), cx, top + 4, `11px ${FONT_MONO}`, withAlpha(C.good, 0.8), 0);
    centeredText(ctx, t('overlay.gridSecured'), cx, top + 42, `700 44px ${FONT_DISPLAY}`, C.good, 22);
    centeredText(
      ctx,
      t('overlay.wavesRepelled', { total: CONFIG.waves.length }),
      cx,
      top + 72,
      `13px ${FONT_MONO}`,
      C.hudText,
      0,
    );
    overlayScore(ctx, state, cx, top + 96, C.good);
    // Continue → Endless is offered only once Endless is unlocked (§10 / §19.2).
    if (isModeUnlocked(profile, 'endless')) {
      drawActionButton(ctx, WIN_CONTINUE_RECT, t('overlay.continueEndless'), C.accent, time);
    } else {
      centeredText(
        ctx,
        t('overlay.unlockEndless'),
        cx,
        WIN_CONTINUE_RECT.y + 16,
        `10px ${FONT_MONO}`,
        C.hudMuted,
        0,
      );
    }
    centeredText(
      ctx,
      t('overlay.restartCampaign'),
      cx,
      top + 256,
      `11px ${FONT_MONO}`,
      C.hudMuted,
      0,
    );
  } else {
    // Defeat — campaign or endless.
    drawPanel(ctx, cx, cy, 468, 326, C.bad, time);
    const top = cy - 130;
    centeredText(ctx, t('overlay.systemFailure'), cx, top + 4, `11px ${FONT_MONO}`, withAlpha(C.bad, 0.8), 0);
    centeredText(ctx, t('overlay.coreBreached'), cx, top + 44, `700 44px ${FONT_DISPLAY}`, C.bad, 22);
    centeredText(
      ctx,
      state.mode === 'endless'
        ? t('overlay.endlessEnded', { wave: state.wave })
        : t('overlay.reactorOverrun', { wave: state.wave, total: CONFIG.waves.length }),
      cx,
      top + 76,
      `13px ${FONT_MONO}`,
      C.hudText,
      0,
    );
    overlayScore(ctx, state, cx, top + 100, C.bad);
    centeredText(
      ctx,
      t('overlay.runItBack'),
      cx,
      top + 236,
      `700 13px ${FONT_MONO}`,
      withAlpha(C.bad, pulse),
      8 * pulse,
    );
  }
  ctx.restore();
}

/** The shared score block on the win / lose panels: divider, hero number, record. */
function overlayScore(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  cx: number,
  dividerY: number,
  accent: string,
): void {
  ctx.strokeStyle = withAlpha(accent, 0.4);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - 170, dividerY);
  ctx.lineTo(cx + 170, dividerY);
  ctx.stroke();

  centeredText(ctx, spaced(t('overlay.finalScore')), cx, dividerY + 24, `10px ${FONT_MONO}`, C.hudMuted, 0);
  centeredText(
    ctx,
    String(state.finalScore),
    cx,
    dividerY + 62,
    `700 46px ${FONT_DISPLAY}`,
    C.hudText,
    16,
  );

  const record = state.finalScore > 0 && state.finalScore >= state.highScore;
  if (record) {
    const badge = state.mode === 'endless' ? t('overlay.newEndlessBest') : t('overlay.newHighScore');
    ctx.font = `700 12px ${FONT_MONO}`;
    const bw = ctx.measureText(badge).width + 28;
    ctx.fillStyle = withAlpha('#f7d51d', 0.16);
    roundRect(ctx, cx - bw / 2, dividerY + 82, bw, 24, 12);
    ctx.fill();
    centeredText(ctx, badge, cx, dividerY + 98, `700 12px ${FONT_MONO}`, '#f7d51d', 10);
  } else {
    const label = state.mode === 'endless' ? t('overlay.endlessBest') : t('overlay.highScore');
    centeredText(
      ctx,
      `${label}   ${state.highScore}`,
      cx,
      dividerY + 96,
      `12px ${FONT_MONO}`,
      C.hudMuted,
      0,
    );
  }
}

/** A pulsing neon call-to-action button (used on the win screen). */
function drawActionButton(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  label: string,
  color: string,
  time: number,
): void {
  const pulse = 0.6 + 0.4 * Math.sin(time * 3);
  ctx.save();
  const g = ctx.createLinearGradient(0, r.y, 0, r.y + r.h);
  g.addColorStop(0, withAlpha(color, 0.24));
  g.addColorStop(1, withAlpha(color, 0.08));
  ctx.fillStyle = g;
  roundRect(ctx, r.x, r.y, r.w, r.h, 7);
  ctx.fill();
  ctx.strokeStyle = withAlpha(color, 0.55 + 0.4 * pulse);
  ctx.lineWidth = 1.7;
  ctx.shadowBlur = 12 * pulse;
  ctx.shadowColor = color;
  roundRect(ctx, r.x, r.y, r.w, r.h, 7);
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.font = `700 14px ${FONT_DISPLAY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = color;
  ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2 + 1);
  ctx.restore();
}

/** A neon dialog panel with corner brackets. */
function drawPanel(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  w: number,
  h: number,
  color: string,
  time: number,
): void {
  const x = cx - w / 2;
  const y = cy - h / 2;
  ctx.save();
  const g = ctx.createLinearGradient(0, y, 0, y + h);
  g.addColorStop(0, 'rgba(14, 22, 42, 0.96)');
  g.addColorStop(1, 'rgba(8, 12, 24, 0.96)');
  ctx.fillStyle = g;
  roundRect(ctx, x, y, w, h, 12);
  ctx.fill();
  ctx.strokeStyle = withAlpha(color, 0.55 + 0.25 * Math.sin(time * 2.5));
  ctx.lineWidth = 1.5;
  ctx.shadowBlur = 22;
  ctx.shadowColor = color;
  roundRect(ctx, x, y, w, h, 12);
  ctx.stroke();
  ctx.shadowBlur = 0;

  // Corner brackets.
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.5;
  const k = 18;
  const corner = (px: number, py: number, sx: number, sy: number): void => {
    ctx.beginPath();
    ctx.moveTo(px + sx * k, py);
    ctx.lineTo(px, py);
    ctx.lineTo(px, py + sy * k);
    ctx.stroke();
  };
  corner(x + 7, y + 7, 1, 1);
  corner(x + w - 7, y + 7, -1, 1);
  corner(x + 7, y + h - 7, 1, -1);
  corner(x + w - 7, y + h - 7, -1, -1);
  ctx.restore();
}

/** Draw centred text with an optional glow. */
function centeredText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  font: string,
  color: string,
  glow: number,
): void {
  ctx.save();
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = color;
  if (glow > 0) {
    ctx.shadowBlur = glow;
    ctx.shadowColor = color;
  }
  ctx.fillText(text, x, y);
  ctx.restore();
}
